import { Theme } from "@mui/material/styles/createTheme";
import { alpha } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import color from "color";
import { control, motion, radius, transition } from "./tokens";
import { compactScrollbar } from "./scrollbars";
import { buttonRoleSx } from "./buttonRoles";

const lightThemeColor = JSON.parse(
  localStorage.getItem("lightThemeColor") || "{}"
);

/**
 * Redesign note — radii.
 *
 * These three still read the installation's saved Theme Color settings first,
 * so a client that configured its own radii keeps them. Only the DEFAULTS
 * moved, from 4px/4px/none to the design-system scale in theme/tokens.ts. That
 * one change re-rounds every button, control and card in 195 files at once.
 */
const setBRadius = lightThemeColor?.borderRadius || `${radius.md}px`;

/** True when the active theme is the light one. */
const isLightTheme = (theme: Theme) => theme.palette.mode === "light";

/**
 * The ink for a soft action button. Runs darker than its surface in light mode
 * and lighter in dark, so amber on an amber wash still clears contrast instead
 * of glowing.
 */
const actionInk = (theme: Theme, family: "info" | "warning" | "error" | "success") => {
  const swatch = theme.palette[family];
  return isLightTheme(theme) ? swatch.dark || swatch.main : swatch.light || swatch.main;
};

/**
 * Geometry and motion shared by every row action button, so the three semantic
 * classes below differ only in colour.
 */
const actionButtonBase = (theme: Theme) => ({
  minWidth: control.action,
  width: control.action,
  height: control.action,
  padding: 0,
  borderRadius: `${radius.md}px`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
  transition: transition(
    ["color", "background-color", "border-color", "transform"],
    motion.fast
  ),
  "& .MuiSvgIcon-root": { fontSize: 17 },
});

const setButtonBRadius = lightThemeColor?.IconButtonBorderRadius || `${radius.sm}px`;

const setCardBRadius = lightThemeColor?.cardBorderRadius || `${radius.xl}px`;

const orderListButtonBRadius =
  lightThemeColor?.orderListProp?.buttonRadius || "4px";
const orderListButtoCardBRadius =
  lightThemeColor?.orderListProp?.cardBorderRadius || "none";
const orderListHeadingFontSize =
  lightThemeColor?.orderListProp?.headingFontSize || "21px";

const meetingLableBR =
  lightThemeColor?.orderListProp?.meetingBorderRadius || "4px";

export const useStyles = makeStyles((theme: Theme) => ({
  /* Topbar: a quiet glass band rather than a solid slab. The colour is still
     whatever Setting → Theme Color configured; only the depth treatment, the
     control sizing and the hover/focus states are new. */
  topbar: {
    top: 0,
    zIndex: 1005,
    height: "76px",
    boxShadow: `0 1px 2px ${color(theme.palette.common.black).alpha(0.04).string()}`,
    borderBottom: `1px solid ${color(theme.palette.divider).alpha(0.75).string()}`,
    backgroundColor: color(theme.palette.topbarButton.topbarBg).alpha(0.88).string(),
    backdropFilter: "saturate(180%) blur(12px)",
    WebkitBackdropFilter: "saturate(180%) blur(12px)",
    [theme.breakpoints.down("md")]: {
      paddingLeft: "0px",
    },
    margin: "0px",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    minHeight: "76px",
    height: "76px",
    margin: 0,
    padding: "0 20px",
    [theme.breakpoints.down("sm")]: {
      padding: "0 12px",
    },
  },
  mobileTopbar: {
    top: 0,
    zIndex: 1005,
    height: "76px",
    boxShadow: `0 1px 2px ${color(theme.palette.common.black).alpha(0.04).string()}`,
    borderBottom: `1px solid ${color(theme.palette.divider).alpha(0.75).string()}`,
    backgroundColor: theme.palette.cardAdd.default,
    margin: "0px",
  },
  /* One hit area, one hover wash, one focus ring — shared by every icon-only
     control in the topbar so the cluster reads as a single group. */
  topbarButton: {
    border: "none",
    borderRadius: "12px",
    width: "40px",
    height: "40px",
    padding: 0,
    backgroundColor: "transparent",
    transition: "background-color 180ms ease, color 180ms ease",
    color: theme.palette.topbarButton.topbarItemColor,
    position: "relative",
    "&:hover": {
      backgroundColor: color(theme.palette.primary.main).alpha(0.08).string(),
      color: theme.palette.primary.main,
    },
    "&.show, &.active, &:active": {
      color: theme.palette.primary.main,
    },
    "&:focus-visible": {
      outline: `2px solid ${color(theme.palette.primary.main).alpha(0.7).string()}`,
      outlineOffset: "2px",
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },
  },
  topbarIcon: {
    width: "1.375rem",
    height: "1.375rem",
    borderRadius: "50%",
  },
  topbarImgButton: {
    border: "none",
    borderRadius: "12px",
    width: "40px",
    height: "40px",
    padding: 0,
    backgroundColor: "transparent",
    transition: "background-color 180ms ease, color 180ms ease",
    color: theme.palette.topbarButton.topbarItemColor,
    position: "relative",
    "&:hover": {
      backgroundColor: color(theme.palette.primary.main).alpha(0.08).string(),
      color: theme.palette.primary.main,
    },
    "&.show, &.active, &:active": {
      color: theme.palette.primary.main,
    },
    "&:focus-visible": {
      outline: `2px solid ${color(theme.palette.primary.main).alpha(0.7).string()}`,
      outlineOffset: "2px",
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },
  },
  topbarImgIcon: {
    width: "2rem",
    height: "2rem",
    borderRadius: "50%",
  },
  topbarIconProfile: {
    width: "2rem",
    height: "2rem",
    borderRadius: "50%",
  },
  topbarIconProfileImg: {
    width: "2.5rem",
    height: "2.5rem",
    borderRadius: "50%",
  },
  topbarTittle: {
    color: "#5d7186",
    height: "1.7rem",
    borderRadius: "50%",
  },
  appSearch: {
    position: "relative",
    marginRight: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  formControl: {
    border: "none",
    paddingLeft: "40px",
    paddingRight: "15px",
    backgroundColor: theme.palette.topbarButton.topbarSearchBg,
    boxShadow: "none",
    height: "42px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  /* Filter-bar controls. Raised from 34px to the 40px system height so a search
     box, a select and a button in the same toolbar line up on one baseline —
     the mixed 33/34/45/56px heights were the main reason those rows looked
     assembled rather than designed. */
  formControlList: {
    border: "none",
    paddingLeft: "40px",
    paddingRight: "15px",
    boxShadow: "none",
    height: `${control.md}px`,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        // border: "none",
      },
      "&.Mui-focused fieldset": {
        // border: "none",
      },
    },
    // [theme.breakpoints.down("sm")]: {
    //   display: "none",
    // },
  },

  formControlListStatus: {
    border: "none",
    boxShadow: "none",
    borderRadius: `${radius.md}px`,
    height: `${control.md}px`,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        // border: "none",
      },
      "&.Mui-focused fieldset": {
        // border: "none",
      },
    },
    // [theme.breakpoints.down("sm")]: {
    //   display: "none",
    // },
  },

  datePickerProper: {
    fontSize: 13.5,
    /* Was a hardcoded pale blue that failed contrast in both modes. */
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.cardAdd.secBackColor,
  },

  formControlListPayment: {
    border: "none",
    paddingLeft: "5px",
    paddingRight: "15px",
    backgroundColor: theme.palette.topbarButton.topbarSearchBg,
    boxShadow: "none",
    height: "34px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
  },

  searchWidgetIcon: {
    position: "absolute",
    zIndex: 10,
    fontSize: "25px",
    left: "11px",
    top: "50%",
    // color: theme.palette.primary.main,
    transform: "translateY(-50%)",
    cursor: "pointer",
  },
  searchWidgetIcon2: {
    position: "absolute",
    zIndex: 10,
    fontSize: "25px",
    left: "11px",
    top: "50%",
    // color: theme.palette.primary.main,
    transform: "translateY(-50%)",
    cursor: "pointer",
  },
  searchWidgetIconPrimary: {
    position: "absolute",
    zIndex: 10,
    fontSize: "25px",
    left: "11px",
    top: "50%",
    color: theme.palette.cardAdd.secBackColor,
    transform: "translateY(-50%)",
    cursor: "pointer",
  },
  searchWidgetIcon23: {
    position: "absolute",
    zIndex: 10,
    fontSize: "25px",
    left: "11px",
    top: "50%",
    color: theme.palette.primary.main,
    transform: "translateY(-50%)",
    cursor: "pointer",
  },
  searchTextField: {
    width: "100%",
    backgroundColor: theme.palette.cardAdd.secBackColor,
    // backgroundColor: "#f9fbff",
    // backgroundColor: theme.palette.cardAdd.secBackColor,
    borderRadius: setBRadius,
    "& .MuiOutlinedInput-root": {
      borderRadius: setBRadius,
      // backgroundColor: "#f9fbff",
      // color: theme.palette.primary.main,
      backgroundColor: theme.palette.cardAdd.secBackColor,
      "& fieldset": {
        // border: "none",
      },
      "&:hover fieldset": {
        // border: "none",
      },
      "&.Mui-focused fieldset": {
        // border: "none",
      },
    },
  },
  searchTextFieldPrimary: {
    width: "100%",
    backgroundColor: "#f9fbff",
    // backgroundColor: theme.palette.cardAdd.secBackColor,
    borderRadius: setBRadius,
    "& .MuiOutlinedInput-root": {
      borderRadius: setBRadius,
      // backgroundColor: "#f9fbff",
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.cardAdd.secBackColor,
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
  },
  searchTextField23: {
    width: "100%",
    borderRadius: setBRadius,
    "& .MuiOutlinedInput-root": {
      borderRadius: setBRadius,
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
  },
  searchTextField34: {
    width: "100%",
    // backgroundColor: "#d0eafa",
    // backgroundColor: theme.palette.cardAdd.secBackColor,
    backgroundColor: theme.palette.cardAdd.secBackColor,
    borderRadius: setBRadius,
    "& .MuiOutlinedInput-root": {
      borderRadius: setBRadius,
      // color: theme.palette.primary.main,
      backgroundColor: theme.palette.cardAdd.secBackColor,
      "& fieldset": {
        // border: "none",
      },
      "&:hover fieldset": {
        // border: "none",
      },
      "&.Mui-focused fieldset": {
        // border: "none",
      },
    },
  },
  searchTextField34Primary: {
    width: "100%",
    backgroundColor: "#d0eafa",
    // backgroundColor: theme.palette.cardAdd.secBackColor,
    borderRadius: setBRadius,
    "& .MuiOutlinedInput-root": {
      borderRadius: setBRadius,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.cardAdd.secBackColor,
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
  },

  searchTextField2: {
    width: "100%",
    backgroundColor: "white",
    // backgroundColor: theme.palette.cardAdd.secBackColor,
    borderRadius: setBRadius,
    "& .MuiOutlinedInput-root": {
      borderRadius: setBRadius,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.cardAdd.secBackColor,
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
  },
  searchWidgetIconEdit: {
    position: "absolute",
    zIndex: 10,
    fontSize: "18px",
    left: "11px",
    top: "50%",
    color: "#8486a7",
    transform: "translateY(-50%)",
    cursor: "pointer",
    paddingRight: "2px",
  },
  searchWidgetIconClose: {
    right: "7px",
    left: "auto !important",
  },
  notchedOutline: {
    border: "none",
  },
  drawerHeader: {
    height: "100px",
    backgroundColor: theme.palette.topbarButton.drawerHeader,
    paddingLeft: "75px",
    margin: "0px",
  },

  h1: {
    fontSize: "calc(1.35rem + 1.2vw)",
    fontWeight: 600,
    lineHeight: 1.1,
    color: theme.palette.heading.headingColor,
    "@media (min-width: 1200px)": {
      fontSize: "2.25rem",
    },
  },
  dashboardTypograpy: {
    textTransform: "uppercase",
    color: "#a7a7a7",
    fontWeight: 550,
    lineHeight: 1.1,
    fontSize: "16px",
  },
  dashboardTypograpy2: {
    fontWeight: 550,
    fontSize: "17px",
  },
  dashboardTypograpy23: {
    fontSize: "15px",
  },
  h2: {
    fontSize: "calc(1.3125rem + 0.75vw)",
    fontWeight: 600,
    lineHeight: 1.1,
    color: theme.palette.heading.headingColor,
    "@media (min-width: 1200px)": {
      fontSize: "1.875rem",
    },
  },
  h3: {
    fontSize: "calc(1.2rem + 0.3vw)",
    fontWeight: 600,
    lineHeight: 1.1,
    color: theme.palette.heading.headingColor,
    "@media (min-width: 1200px)": {
      fontSize: "1.5rem",
    },
  },
  h3Heading: {
    fontWeight: 600,
    lineHeight: 1.1,
    color: "#313b5e",
    fontSize: "20px",
  },
  h4: {
    fontSize: "1.125rem",
    fontWeight: 600,
    lineHeight: "21px",
    color: theme.palette.heading.headingColor,
    // textTransform: "uppercase",
  },
  PaymentTypograpy: {
    fontSize: "1.125rem",
    fontWeight: 500,
    lineHeight: "21px",
    color: theme.palette.heading.headingColor,
    textTransform: "uppercase",
  },
  h4Text: {
    fontSize: "1.125rem",
    fontWeight: 600,
    lineHeight: "21px",
    color: theme.palette.heading.headingColor,
    textTransform: "uppercase",
  },
  h4TextTopBar: {
    fontSize: "1.125rem",
    fontWeight: 700,
    lineHeight: "24px",
    letterSpacing: "-0.01em",
    color: theme.palette.topbarButton.topBarHeading,
  },
  h4Text2: {
    fontSize: "1.3rem",
    fontWeight: 600,
    lineHeight: "21px",
    color: theme.palette.primary.main,
    textTransform: "uppercase",
  },

  h4Text24: {
    fontSize: "1.1rem",
    fontWeight: 400,
    // lineHeight: "21px",
    color: "white",
    // textTransform: "uppercase",
  },
  h5: {
    fontSize: "0.9375rem",
    fontWeight: 600,
    lineHeight: 1.1,
    color: theme.palette.heading.headingColor,
  },
  h6: {
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1.1,
    color: theme.palette.heading.headingColor,
  },
  menuTitle: {
    cursor: "default",
    fontSize: "10.5px",
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    color: theme.palette.topbarButton.drawerHeadingText,
    opacity: 0.55,
    fontWeight: 700,
    padding: `${theme.spacing(1.5)} ${theme.spacing(2)} ${theme.spacing(0.75)}`,
    transition: "all 0.3s ease-in-out",
    // height: "36px",
    margin: 0,
  },
  menuTitle2: {
    cursor: "default",
    fontSize: "13px",
    letterSpacing: "0.05em",
    color: theme.palette.primary.main,
    fontWeight: 600,
    padding: `${theme.spacing(1)} ${theme.spacing(1.25)}`,
    transition: "all 0.3s ease-in-out",
    height: "36px",
    margin: 0,
  },
  listItemIcomMainMenu: {
    margin: 0,
    width: 30,
    marginLeft: 0,
    color: theme.palette.text.primary,
    opacity: 0.6,
  },

  body1: {
    fontSize: "1rem",
    lineHeight: 1.5,
    // marginBottom: "1rem",
    textAlign: "center",
    margin: 0,
    textTransform: "uppercase",
  },
  bodyDialog: {
    fontSize: "1rem",
    lineHeight: 1.5,
    // marginBottom: "1rem",
    textAlign: "center",
    margin: 0,
    // textTransform: "uppercase",
  },
  bodyDialog23: {
    fontSize: "1rem",
    lineHeight: 1.5,
    // marginBottom: "1rem",
    margin: 0,
    // textTransform: "uppercase",
  },
  body2: {
    fontSize: "0.875rem",
    lineHeight: 1.13,
    marginBottom: "0.5rem",
  },
  subtitle1: {
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 1.57,
    marginBottom: "0.5rem",
  },
  subtitle2: {
    fontSize: "0.75rem",
    fontWeight: 500,
    lineHeight: 1.57,
    marginBottom: "0.5rem",
  },
  button: {
    fontSize: "0.875rem",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  caption: {
    fontSize: "0.75rem",
    lineHeight: 1.66,
  },
  overline: {
    fontSize: "0.75rem",
    fontWeight: 700,
    lineHeight: 2.66,
    textTransform: "uppercase",
  },

  navbarHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    margin: "0 auto",
  },

  searchIcon: {
    position: "absolute",
    zIndex: 10,
    fontSize: 18,
    left: 11,
    top: "50%",
    color: "#8486a7",
    transform: "translateY(-50%)",
    cursor: "pointer",
  },
  inputRoot: {
    color: "inherit",
  },
  inputInput: {
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    paddingRight: theme.spacing(1),
    backgroundColor: "var(--bs-topbar-search-bg)",
    border: "none",
    boxShadow: "none",
    height: 42,
    width: "100%",
  },
  buttonToggleMenu: {
    color: "var(--bs-topbar-item-color)",
    backgroundColor: "transparent",
    borderRadius: 0,
    "&:hover": {
      color: "#ff6c2f",
    },
  },
  topbarItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "var(--bs-topbar-height)",
  },
  /* The default panel. A hairline plus a short contact shadow, so a card reads
     as a surface sitting on the page rather than as a white rectangle butted
     against it. This one class is the most widely used in the app. */
  card: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    p: "24px",
    borderRadius: setCardBRadius,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow:
      theme.palette.mode === "light"
        ? "0 1px 2px rgba(16, 32, 52, 0.04), 0 1px 3px rgba(16, 32, 52, 0.03)"
        : "0 1px 2px rgba(0, 0, 0, 0.3)",
    overflow: "hidden",
  },
  drawerTodo: {
    backgroundColor: theme.palette.cardAdd.bgColor,
  },
  cardPrimary: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    p: "24px",
    borderBottom: `5px solid ${theme.palette.primary.main}`,
    borderRadius: setCardBRadius,
    boxShadow: "none",
    overflow: "hidden",
  },

  cardGreen: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    p: "24px",
    borderBottom: `5px solid #007f00`,
    borderRadius: setCardBRadius,
    boxShadow: "none",
    overflow: "hidden",
  },
  cardGreen24: {
    backgroundColor: "#f4fffa",
    p: "24px",
    borderBottom: `5px solid #007f00`,
    borderRadius: "none",
    boxShadow: "none",
  },
  cardGreenBAckGroundColor: {
    backgroundColor: theme.palette.cardAdd.secBackColor,
    p: "24px",
    borderBottom: `3px solid ${theme.palette.primary.main}`,
    borderRadius: "none",
    boxShadow: "none",
  },
  cardGreenBAckGroundColor12: {
    backgroundColor: "#fff7f1",
    p: "24px",
    borderBottom: `5px solid #df6800`,
    borderRadius: "none",
    boxShadow: "none",
  },
  cardGreenTypograpy: {
    color: "#274057",
    fontSize: "15px",
  },
  cardRed: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    p: "24px",
    borderBottom: `5px solid #d30000`,
    borderRadius: setCardBRadius,
    boxShadow: "none",
  },
  cardRed2: {
    backgroundColor: "#fff2f2",
    p: "24px",
    borderBottom: `5px solid #d73a3c`,
    borderRadius: "none",
    boxShadow: "none",
  },
  cardRed212: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    p: "24px",
    borderBottom: `5px solid #d73a3c`,
    borderRadius: "none",
    boxShadow: "none",
  },
  cardRed23: {
    backgroundColor: "#fff2f2",
    p: "24px",
    borderBottom: `5px solid #d30000`,
    borderRadius: "none",
    boxShadow: "none",
  },
  cardYellow: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    p: "24px",
    borderBottom: `5px solid #e1c300`,
    borderRadius: "none",
    boxShadow: "none",
  },
  cardYellow23: {
    backgroundColor: "#fcfbf0",
    p: "24px",
    borderBottom: `5px solid #e1c300`,
    borderRadius: "none",
    boxShadow: "none",
  },
  cardOrrange: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    p: "24px",
    borderBottom: `5px solid #df6800`,
    borderRadius: "none",
    boxShadow: "none",
  },
  cardBlue: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    p: "24px",
    borderBottom: `5px solid #0f5fc2`,
    borderRadius: setCardBRadius,
    boxShadow: "none",
  },
  cardPurple: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    p: "24px",
    borderBottom: `5px solid #5932ea`,
    borderRadius: setCardBRadius,
    boxShadow: "none",
  },
  cardGreen2: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    p: "24px",
    borderBottom: `5px solid ${theme.palette.primary.main}`,
    borderRadius: setCardBRadius,
    boxShadow: "none",
  },
  Dialog: {
    backgroundColor: theme.palette.background.default,
    borderRadius: setBRadius,
    padding: "24px",
  },
  /* Card titles are sentence case now. Uppercase is reserved for the two places
     it earns its keep: table column headers and small section eyebrows. */
  cardTitle: {
    fontSize: "15.5px",
    fontWeight: 660,
    letterSpacing: "-0.01em",
    margin: 0,
    color: theme.palette.text.secondary,
    textTransform: "none",
  },
  cardTitleTab: {
    fontSize: "16px",
    fontWeight: 600,
    margin: 0,
    color: theme.palette.cardAdd.headingColor,
    // textTransform: "uppercase",
    // color: "#bdbec7",
  },
  cardTitle2: {
    fontSize: "16px",
    fontWeight: 600,
    margin: 0,
    textTransform: "uppercase",
    // color: "#bdbec7",
  },
  cardTitle256: {
    fontSize: "16px",
    fontWeight: 600,
    margin: 0,
    // textTransform: "uppercase",
    // color: "#bdbec7",
  },
  ActivityHistoryHEading: {
    fontSize: "16px",
    fontWeight: 600,
    margin: 0,
    color: theme.palette.cardAdd.headingColor,
    // textTransform: "uppercase",
  },
  ActivityHistoryHEading2: {
    fontSize: "16px",
    fontWeight: 600,
    margin: 0,
    // color: theme.palette.cardAdd.headingColor,
    // textTransform: "uppercase",
    color: "#6e94a9",
  },
  ActivityHistoryHEading23: {
    fontSize: "16px",
    fontWeight: 600,
    margin: 0,
    // color: theme.palette.cardAdd.headingColor,
    // textTransform: "uppercase",
    color: theme.palette.primary.main,
  },
  cardTitleContainer: {
    borderBottom: 1,
    padding: 2,
    backgroundColor: theme.palette.cardAdd.bgColor,
  },
  stepperCard: {
    boxShadow: "none",
    backgroundColor: theme.palette.cardAdd.bgColor,
    borderRadius: setCardBRadius,
    fontSize: "16px",
    p: 5,
    fontWeight: 400,
  },

  stepperCardQuilr: {
    boxShadow: "none",
    backgroundColor: theme.palette.cardAdd.bgColor,
    borderRadius: setCardBRadius,
    fontSize: "16px",
    p: 5,
    fontWeight: 400,
  },

  stepperCard3: {
    boxShadow: "none",
    backgroundColor: "#c8f7a1",
    borderRadius: "none",
    fontSize: "16px",
    p: 5,
    fontWeight: 400,
    minHeight: "100vh",
  },

  stepperCard4: {
    boxShadow: "none",
    backgroundColor: "#254506",
    // backgroundColor: "#1e1e1e",
    borderRadius: "none",
    fontSize: "16px",
    p: 5,
    fontWeight: 400,
    minHeight: "100vh",
  },
  stepperCard5: {
    boxShadow: "none",
    backgroundColor: "#f7ffef",
    // backgroundColor: "#1e1e1e",
    borderRadius: "none",
    fontSize: "16px",
    p: 5,
    fontWeight: 400,
    minHeight: "100vh",
  },
  stepperCard2: {
    boxShadow: "none",
    backgroundColor: theme.palette.cardAdd.bgColor,
    borderRadius: "20px",
    fontSize: "16px",
    p: 5,
    fontWeight: 400,
  },
  stepperCardGrid: {
    boxShadow: "none",
    borderRadius: "8px",
    fontSize: "16px",
    p: 5,
    fontWeight: 400,
    display: "flex",
    flexDirection: "column",
  },
  addButtonList: {
    borderBottom: 1,
    padding: 2,
    backgroundColor: theme.palette.cardAdd.bgColor,
  },

  addButtonList1: {
    borderBottom: 1,
    padding: 2,
    backgroundColor: theme.palette.cardAdd.bgColor,
    textTransform: "uppercase",
    width: "100%",
  },
  /* The workhorse button class, used on nearly every screen. It no longer sets
     a colour of its own - the MUI variant decides that - so a `contained`
     button is brand blue and an `outlined` one is a real bordered surface
     rather than the ghost it used to render as. */
  addButton: {
    paddingLeft: "16px",
    paddingRight: "16px",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "13.5px",
    lineHeight: 1.5,
    minHeight: control.md,
    borderRadius: setBRadius,
    boxShadow: "none",
    cursor: "pointer",
    transition: transition(
      ["color", "background-color", "border-color", "box-shadow"],
      motion.fast
    ),
    "&:hover": {
      boxShadow: "none",
    },
    /* A real, visible keyboard ring. The old rule referenced a Bootstrap CSS
       variable that does not exist in this app, so focus was invisible. */
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.primary.main, 0.55)}`,
      outlineOffset: 2,
    },
    "&:disabled": {
      opacity: 0.55,
    },
  },
  addButtonEdit: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    lineHeight: 1.5,
    backgroundColor: "#ff6c2f",
    borderRadius: setBRadius,
    // boxShadow: "0px 2px 6px 0px",
    // color: '#aab8c5',
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      borderColor: "transparent",
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  /* The page-level primary action ("Add Client", "Add Policy", "Add New", ...),
     used on 41 screens.

     It used to paint `iconButton.addButtonColor` directly. That slot is
     configured as `#7a7f87` in this installation, so every primary action in
     the product rendered as a dull slate block — the Meetings "Add New" and
     "Setting" buttons in particular read as disabled. The colour now goes
     through `buttonRole("primary", solid)`, which prefers the configured value
     and falls back to the brand primary when that value is too close to grey to
     carry an action. Nothing is written back and no hex is introduced here; see
     `theme/buttonRoles.ts` for why the fallback exists. */
  addNewButton: {
    paddingLeft: "16px",
    paddingRight: "16px",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "13.5px",
    lineHeight: 1.5,
    minHeight: control.md,
    borderRadius: setBRadius,
    boxShadow: "none",
    cursor: "pointer",
    transition: transition(
      ["color", "background-color", "border-color", "box-shadow"],
      motion.fast
    ),
    ...buttonRoleSx(theme, "primary", true),
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.primary.main, 0.55)}`,
      outlineOffset: 2,
    },
    "&:disabled": {
      opacity: 0.55,
    },
  },
  /* The supporting action beside a primary one — Setting, Reset, Cancel, Back.
     Same geometry as `addNewButton` so the two sit level in a toolbar; a real
     bordered surface rather than a second filled block, so a screen has one
     obvious main action instead of two competing ones. */
  neutralButton: {
    paddingLeft: "16px",
    paddingRight: "16px",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "13.5px",
    lineHeight: 1.5,
    minHeight: control.md,
    borderRadius: setBRadius,
    boxShadow: "none",
    cursor: "pointer",
    transition: transition(
      ["color", "background-color", "border-color", "box-shadow"],
      motion.fast
    ),
    ...buttonRoleSx(theme, "neutral"),
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.primary.main, 0.55)}`,
      outlineOffset: 2,
    },
    "&:disabled": {
      opacity: 0.55,
    },
  },

  /* Export / download / generate-report actions.

     Same geometry as `addNewButton`, different meaning: a download is a
     positive utility action, not the page's dominant one, so it takes the
     success family in the tinted treatment. This is the appearance the Policy
     Report's "Export to Excel" already had, and it is now the one export style
     for the product — a screen with an Export beside an Add New reads as one
     primary action and one utility, rather than two equal blue blocks. */
  exportButton: {
    paddingLeft: "16px",
    paddingRight: "16px",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "13.5px",
    lineHeight: 1.5,
    minHeight: control.md,
    borderRadius: setBRadius,
    boxShadow: "none",
    cursor: "pointer",
    transition: transition(
      ["color", "background-color", "border-color", "box-shadow"],
      motion.fast
    ),
    ...buttonRoleSx(theme, "success"),
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.success.main, 0.55)}`,
      outlineOffset: 2,
    },
    "&:disabled": {
      opacity: 0.55,
    },
  },

  /* The PDF / Excel download buttons. A download is a positive utility action,
     not the page's dominant one, so it takes the success family in the tinted
     treatment — the same appearance as the Policy Report's "Export to Excel",
     which is the reference this standardises on. */
  addButtonNewPdfExcel: {
    paddingLeft: "14px",
    paddingRight: "14px",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "13px",
    lineHeight: 1.5,
    minHeight: control.md,
    borderRadius: setBRadius,
    minWidth: "84px",
    boxShadow: "none",
    cursor: "pointer",
    transition: transition(
      ["color", "background-color", "border-color"],
      motion.fast
    ),
    ...buttonRoleSx(theme, "success"),
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.success.main, 0.55)}`,
      outlineOffset: 2,
    },
    "&:disabled": {
      opacity: 0.55,
    },
  },
  addButton2: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    lineHeight: 1.5,
    height: "45px",
    // backgroundColor: "#ff6c2f",
    // borderRadius: "15px",
    // boxShadow: "0px 2px 6px 0px",
    // color: '#aab8c5',
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      borderColor: "transparent",
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  addButton1: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    fontWeight: 600,
    lineHeight: 1.5,
    textTransform: "uppercase",
    width: "100%",
    height: "45px",
    // backgroundColor: "#ff6c2f",
    // borderRadius: "15px",
    // boxShadow: "0px 2px 6px 0px",
    // color: '#aab8c5',
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      borderColor: "transparent",
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  actionButton: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    lineHeight: 1.5,
    minWidth: "150px",
    backgroundColor: theme.palette.cardAdd.secBackColor,
    borderRadius: setBRadius,
    // boxShadow: "0px 2px 6px 0px",
    color: "#99bcd1",
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      borderColor: "transparent",
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  actionButtonTwo: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    lineHeight: 1.5,
    minWidth: "150px",
    minHeight: "40px",
    backgroundColor: "#4eccc5",
    borderRadius: setBRadius,
    // boxShadow: "0px 2px 6px 0px",
    color: "white",
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      borderColor: "transparent",
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  /* Export controls (PDF / Excel). Secondary weight everywhere: a bordered
     surface, never a second primary competing with the page action. */
  addButtonPdfExcel: {
    paddingLeft: "14px",
    paddingRight: "14px",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "13px",
    lineHeight: 1.5,
    minHeight: control.md,
    borderRadius: setBRadius,
    minWidth: "84px",
    boxShadow: "none",
    cursor: "pointer",
    transition: transition(
      ["color", "background-color", "border-color"],
      motion.fast
    ),
    /* The compact "Excel" / "pdf" pair on Attendance, Payroll, Staff and the
       time-schedule reports. They were a neutral surface, which made a download
       look like a supporting control; they now carry the same success family as
       every other export in the product. */
    ...buttonRoleSx(theme, "success"),
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.success.main, 0.55)}`,
      outlineOffset: 2,
    },
    "&:disabled": {
      opacity: 0.55,
    },
  },
  textField: {
    flexGrow: 1,
    width: "100%",
    fontSize: 14,
  },
  textField2: {
    flexGrow: 1,
    width: "100%",
    fontSize: "1.1rem",
    fontWeight: 400,
    lineHeight: "21px",
    color: "white",
  },
  textFieldQuote2: {
    flexGrow: 1,
    width: "100%",
    fontSize: "1.1rem",
    fontWeight: 400,
    lineHeight: "21px",
    color: "white",
    // borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
  },
  textField200: {
    flexGrow: 1,
    width: "100%",
    fontSize: 14,
    minWidth: "100px",
  },
  gridBordrBottomColor: {
    borderBottom: 1,
    padding: 2,
    borderColor: theme.palette.divider,
  },
  table: {
    width: "100%",
    marginBottom: 0,
    verticalAlign: "middle",
    borderCollapse: "separate",
    borderSpacing: 0,
    "& > :not(caption) > * > *": {
      /* Enterprise density. 12px vertical puts a single-line row at ~52px:
         efficient enough to scan a full page of brokerage data, loose enough
         to read. The old 0.85rem all round made rows feel like a spreadsheet. */
      padding: "12px 16px",
    },
  },

  tableHeaderRow: {
    textTransform: "uppercase",
    /* Was a hardcoded #e0e1e5 - near-invisible on the light header band. */
    color: theme.palette.text.primary,
    fontSize: "11.5px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    background: theme.palette.cardAdd.secBackColor,
    "& th, & td": {
      whiteSpace: "nowrap",
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  tablCell: {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
  },
  tablCell1: {
    border: `1px solid #af9a8e`,
    borderRadius: "8px",
  },

  tablCell12: {
    borderBottom: `1px solid #af9a8e`,
  },

  tablCellpx: {
    borderBottom: `2px solid ${theme.palette.cardAdd.borderColor}`,
  },

  tablheader: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderTop: "none",
    color: theme.palette.text.primary,
    fontSize: "11.5px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  primaryHeading: {
    color: theme.palette.primary.main,
    textTransform: "uppercase",
    fontWeight: 600,
    fontSize: "15px",
  },
  primaryHeading12: {
    color: "#fef3c7",
    textTransform: "uppercase",
    fontWeight: 600,
    fontSize: "15px",
  },
  tablheaderCreate: {
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
    color: "#e0e1e5",
  },
  tablheaderCreate160: {
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
    maxWidth: "160px",
    color: "#e0e1e5",
  },

  tableRowHover: {
    transition: transition(["background-color"], motion.fast),
    "&:hover": {
      backgroundColor: theme.palette.cardAdd.tablehover,
    },
    /* The last row keeps the card's own rounded edge instead of drawing a rule
       across it. */
    "&:last-child td, &:last-child th": {
      borderBottom: "none",
    },
  },

  /* --------------------------------------------------------------------
     Row action buttons.

     These three classes are used by ~65 list and detail screens, so they are
     the one place the whole portal's row actions can be brought into the same
     language. They used to be filled blocks built from hardcoded hexes
     (#ef5f5f, #99c9cf6e, #323a46) at opacity 0.65 — a delete carried as much
     weight as a view, and none of it followed the configured theme.

     Now each is a soft semantic surface: cyan for view, amber for edit, rose
     for delete, every colour read from the palette. Delete is deliberately the
     quietest so a destructive action is never the loudest thing in a row.

     Matches Components/Design/TableActions.tsx exactly, so pages on the shared
     component and pages still on these classes look identical.
     -------------------------------------------------------------------- */
  editButton: {
    ...actionButtonBase(theme),
    color: actionInk(theme, "warning"),
    border: `1px solid ${alpha(theme.palette.warning.main, isLightTheme(theme) ? 0.22 : 0.34)}`,
    backgroundColor: alpha(theme.palette.warning.main, isLightTheme(theme) ? 0.1 : 0.18),
    "&:hover": {
      backgroundColor: alpha(theme.palette.warning.main, isLightTheme(theme) ? 0.18 : 0.3),
      borderColor: alpha(theme.palette.warning.main, isLightTheme(theme) ? 0.45 : 0.55),
      transform: "translateY(-1px)",
      "& .MuiSvgIcon-root": { color: actionInk(theme, "warning") },
    },
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.warning.main, 0.6)}`,
      outlineOffset: 2,
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover": { transform: "none" },
    },
  },

  deleteButton: {
    ...actionButtonBase(theme),
    color: actionInk(theme, "error"),
    /* One step quieter than view and edit — see the note above. */
    border: `1px solid ${alpha(theme.palette.error.main, isLightTheme(theme) ? 0.16 : 0.28)}`,
    backgroundColor: alpha(theme.palette.error.main, isLightTheme(theme) ? 0.07 : 0.13),
    "&:hover": {
      backgroundColor: alpha(theme.palette.error.main, isLightTheme(theme) ? 0.14 : 0.24),
      borderColor: alpha(theme.palette.error.main, isLightTheme(theme) ? 0.45 : 0.55),
      transform: "translateY(-1px)",
      "& .MuiSvgIcon-root": { color: actionInk(theme, "error") },
    },
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.error.main, 0.6)}`,
      outlineOffset: 2,
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover": { transform: "none" },
    },
  },
  viewButton: {
    ...actionButtonBase(theme),
    /* Semantic cyan rather than the configured primary: an installation whose
       dark-mode primary is a desaturated grey would otherwise show a grey view
       button beside a coloured edit and delete. */
    color: actionInk(theme, "info"),
    border: `1px solid ${alpha(theme.palette.info.main, isLightTheme(theme) ? 0.22 : 0.34)}`,
    backgroundColor: alpha(theme.palette.info.main, isLightTheme(theme) ? 0.1 : 0.18),
    "&:hover": {
      backgroundColor: alpha(theme.palette.info.main, isLightTheme(theme) ? 0.18 : 0.3),
      borderColor: alpha(theme.palette.info.main, isLightTheme(theme) ? 0.45 : 0.55),
      transform: "translateY(-1px)",
      "& .MuiSvgIcon-root": { color: actionInk(theme, "info") },
    },
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.info.main, 0.6)}`,
      outlineOffset: 2,
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover": { transform: "none" },
    },
  },
  editButtonTenantList: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: setButtonBRadius,
    opacity: 0.65,
    color: "white",
    border: "none",
    padding: 1,
    minWidth: 44,
    height: 33,
    display: "flex",
    justifyContent: "center",
    backgroundColor: theme.palette.primary.main,
    alignItems: "center",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      "& .MuiSvgIcon-root": {
        color: "white",
      },
    },
    boxShadow: "none",
  },
  editButtonList: {
    /* Row action - see the note on viewButtonList. */
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: 1.5,
    borderRadius: `${radius.md}px`,
    border: `1px solid ${alpha(theme.palette.warning.main, isLightTheme(theme) ? 0.22 : 0.34)}`,
    backgroundColor: alpha(theme.palette.warning.main, isLightTheme(theme) ? 0.1 : 0.18),
    color: actionInk(theme, "warning"),
    padding: 0,
    minWidth: control.action,
    width: control.action,
    height: control.action,
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "none",
    transition: transition(["color", "background-color", "border-color"], motion.fast),
    "& .MuiSvgIcon-root": { fontSize: 17, color: "inherit" },
    "&:hover": {
      color: actionInk(theme, "warning"),
      borderColor: alpha(theme.palette.warning.main, isLightTheme(theme) ? 0.45 : 0.55),
      backgroundColor: alpha(theme.palette.warning.main, isLightTheme(theme) ? 0.18 : 0.3),
      "& .MuiSvgIcon-root": { color: "inherit" },
    },
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.warning.main, 0.6)}`,
      outlineOffset: 2,
    },
  },
  saveButtonList: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: setButtonBRadius,
    padding: 1,
    minWidth: 44,
    height: 33,
    display: "flex",
    justifyContent: "center",
    // backgroundColor: theme.palette.iconButton.editButtonColor,
    alignItems: "center",
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      borderColor: "transparent",
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  editButtonListGridTable: {
    /* Row action, grid-table variant. */
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: 1.5,
    borderRadius: setButtonBRadius,
    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    backgroundColor: theme.palette.cardAdd.bgColor,
    color: theme.palette.text.primary,
    padding: 0,
    minWidth: control.action,
    width: control.action,
    height: control.action,
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "none",
    transition: transition(["color", "background-color", "border-color"], motion.fast),
    "& .MuiSvgIcon-root": { fontSize: 17, color: "inherit" },
    "&:hover": {
      color: theme.palette.primary.main,
      borderColor: alpha(theme.palette.primary.main, 0.45),
      backgroundColor: alpha(theme.palette.primary.main, 0.09),
      "& .MuiSvgIcon-root": { color: "inherit" },
    },
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.primary.main, 0.6)}`,
      outlineOffset: 2,
    },
  },
  editListIconAdded: {
    fontSize: "17px",
    color: "inherit",
  },
  deleteButtonList: {
    /* Destructive row action. Carries the same soft-surface language as view
       and edit but a step quieter, so it never dominates the row. */
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: 1.5,
    borderRadius: `${radius.md}px`,
    border: `1px solid ${alpha(theme.palette.error.main, isLightTheme(theme) ? 0.16 : 0.28)}`,
    backgroundColor: alpha(theme.palette.error.main, isLightTheme(theme) ? 0.07 : 0.13),
    color: actionInk(theme, "error"),
    padding: 0,
    minWidth: control.action,
    width: control.action,
    height: control.action,
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "none",
    transition: transition(["color", "background-color", "border-color"], motion.fast),
    "& .MuiSvgIcon-root": { fontSize: 17, color: "inherit" },
    "&:hover": {
      color: actionInk(theme, "error"),
      borderColor: alpha(theme.palette.error.main, isLightTheme(theme) ? 0.45 : 0.55),
      backgroundColor: alpha(theme.palette.error.main, isLightTheme(theme) ? 0.14 : 0.24),
      "& .MuiSvgIcon-root": { color: "inherit" },
    },
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.error.main, 0.6)}`,
      outlineOffset: 2,
    },
  },
  deleteButtonListGridTable: {
    /* Destructive row action, grid-table variant. */
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: 1.5,
    borderRadius: setButtonBRadius,
    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    backgroundColor: theme.palette.cardAdd.bgColor,
    color: theme.palette.text.primary,
    padding: 0,
    minWidth: control.action,
    width: control.action,
    height: control.action,
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "none",
    transition: transition(["color", "background-color", "border-color"], motion.fast),
    "& .MuiSvgIcon-root": { fontSize: 17, color: "inherit" },
    "&:hover": {
      color: theme.palette.error.main,
      borderColor: alpha(theme.palette.error.main, 0.45),
      backgroundColor: alpha(theme.palette.error.main, 0.09),
      "& .MuiSvgIcon-root": { color: "inherit" },
    },
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.error.main, 0.6)}`,
      outlineOffset: 2,
    },
  },
  viewButtonList: {
    /* Row actions used to be filled coloured boxes - blue, orange and red
       side by side on every row - so a delete shouted as loudly as a view.
       They then became all-grey squares, which read as disabled. Each now
       carries a soft semantic surface: cyan view, amber edit, rose delete,
       every colour from the palette. The hit area is unchanged, so no table
       reflows.

       Cyan rather than the configured primary, because an installation whose
       dark-mode primary is a desaturated grey would show a grey view button
       beside a coloured edit and delete. */
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: 1.5,
    borderRadius: `${radius.md}px`,
    border: `1px solid ${alpha(theme.palette.info.main, isLightTheme(theme) ? 0.22 : 0.34)}`,
    backgroundColor: alpha(theme.palette.info.main, isLightTheme(theme) ? 0.1 : 0.18),
    color: actionInk(theme, "info"),
    padding: 0,
    minWidth: control.action,
    width: control.action,
    height: control.action,
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "none",
    transition: transition(["color", "background-color", "border-color"], motion.fast),
    "& .MuiSvgIcon-root": { fontSize: 17, color: "inherit" },
    "&:hover": {
      color: actionInk(theme, "info"),
      borderColor: alpha(theme.palette.info.main, isLightTheme(theme) ? 0.45 : 0.55),
      backgroundColor: alpha(theme.palette.info.main, isLightTheme(theme) ? 0.18 : 0.3),
      "& .MuiSvgIcon-root": { color: "inherit" },
    },
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.info.main, 0.6)}`,
      outlineOffset: 2,
    },
  },
  viewButtonListGridTable: {
    /* Row action, grid-table variant. */
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: 1.5,
    borderRadius: setButtonBRadius,
    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    backgroundColor: theme.palette.cardAdd.bgColor,
    color: theme.palette.text.primary,
    padding: 0,
    minWidth: control.action,
    width: control.action,
    height: control.action,
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "none",
    transition: transition(["color", "background-color", "border-color"], motion.fast),
    "& .MuiSvgIcon-root": { fontSize: 17, color: "inherit" },
    "&:hover": {
      color: theme.palette.primary.main,
      borderColor: alpha(theme.palette.primary.main, 0.45),
      backgroundColor: alpha(theme.palette.primary.main, 0.09),
      "& .MuiSvgIcon-root": { color: "inherit" },
    },
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.primary.main, 0.6)}`,
      outlineOffset: 2,
    },
  },
  /* These four now inherit their colour from the button they sit in. They used
     to be hardcoded white, which was correct while the buttons were filled
     coloured boxes and invisible the moment the buttons became light surfaces. */
  editListIcon: {
    fontSize: "17px",
    color: "inherit",
  },
  editListIconG: {
    fontSize: "17px",
    color: "inherit",
  },
  deleteListIcon: {
    fontSize: "17px",
    color: "inherit",
  },
  viewListIcon: {
    fontSize: "17px",
    color: "inherit",
  },
  imgBox: {
    border: "2px dashed",
    borderColor: theme.palette.cardAdd.borderColor,
    borderRadius: "6px",
    cursor: "pointer",
    minHeight: "150px",
    padding: "20px",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  SelectImageBox: {
    width: "100%",
    height: "auto",
    maxWidth: "100px",
    maxHeight: "100px",
    marginRight: "auto",
    objectFit: "cover",
  },
  titleGrid: {
    borderColor: theme.palette.cardAdd.borderColor,
    // borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
  },
  activityHistoryHeaderGrid: {
    borderColor: theme.palette.cardAdd.borderColor,
    // backgroundColor: theme.palette.cardAdd.thirdBgColor,
  },
  activityHistoryHeaderGrid2: {
    borderColor: theme.palette.cardAdd.borderColor,
    backgroundColor: "#d9d9d9",
  },
  titleGridBottomBoredr: {
    borderBottom: `3px solid ${theme.palette.cardAdd.borderColor}`,
  },
  backgroundColorView: {
    backgroundColor: "#f9f9f9",
    borderLeft: `5px solid #007f00`,
  },
  backgroundColorView2: {
    backgroundColor: theme.palette.cardAdd.thirdBgColor,
    borderRadius: setCardBRadius,
  },
  printerBox: {
    border: "2px dashed",
    borderColor: theme.palette.cardAdd.borderColor,
    borderRadius: "6px",
    minHeight: "150px",
    padding: "5px",
  },
  colorBox: {
    border: "2px solid",
    borderColor: theme.palette.cardAdd.borderColor,
    borderRadius: "6px",
    minHeight: "70px",
    padding: "20px",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  barCodeBox: {
    border: "2px solid",
    borderColor: theme.palette.cardAdd.borderColor,
    borderRadius: "6px",
    minHeight: "70px",
    padding: "20px",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  discriptionTextField: {
    flexGrow: 1,
    width: "100%",
    fontSize: 14,
    // backgroundColor: theme.palette.cardAdd.textDescription,
  },
  customerDetails: {
    fontWeight: 800,
    // color: "#5a5a5a",
  },
  iconLinkPropBold: {
    fontWeight: 800,
    cursor: "pointer",
    textDecoration: "none",
    color: "inherit",
  },
  iconLinkPropNormal: {
    cursor: "pointer",
    textDecoration: "none",
    color: "inherit",
  },
  staffTypograpy: {
    color: theme.palette.primary.main,
  },
  customerDiscription: {
    backgroundColor: theme.palette.cardAdd.textDescription,
  },
  productBox: {
    border: "2px solid",
    borderColor: theme.palette.cardAdd.borderColor,
    [theme.breakpoints.down("md")]: {
      height: "200px",
    },
    borderRadius: "6px",
    height: "350px",
    padding: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  productViewPageTableHeaderCell: {
    fontSize: "16px",
    fontWeight: 600,
    borderBottom: 4,
    borderColor: theme.palette.cardAdd.borderColor,
  },
  productBarCodeBox: {
    border: "2px solid",
    borderColor: theme.palette.cardAdd.borderColor,
    borderRadius: "6px",
    padding: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  nameTableList: {
    color: theme.palette.table.nameHeadingColor,
    fontSize: "15px",
    fontWeight: 500,
  },
  SelectImageTable: {
    width: "40px",
    height: "40px",
    border: "none",
    position: "inherit",
  },
  SelectImageTableTwo: {
    width: "120px",
    height: "120px",
    border: "none",
    position: "inherit",
  },
  SelectImageTableList: {
    width: "54px",
    height: "54px",
    border: "none",
    position: "inherit",
  },
  SelectImageTableBox: {
    width: "56px",
    height: "56px",
    p: "5px",
    // border: "1px solid",
    // borderColor: theme.palette.cardAdd.borderColor,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  SelectImageTableBoxList: {
    width: "60px",
    height: "60px",
    p: "5px",
    // border: "1px solid",
    // borderColor: theme.palette.cardAdd.borderColor,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  suiteBox: {
    margin: theme.spacing(1),
    textAlign: "center",
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
    flexDirection: "column",
    cursor: "pointer",
    border: `1px solid ${theme.palette.cardAdd.borderColor}`,
    borderRadius: "8px",
    width: "120px",
    height: "60px",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    transition: "background-color 0.3s, box-shadow 0.3s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      boxShadow: theme.shadows[3],
    },
  },
  suiteBoxBorderNone: {
    margin: theme.spacing(1),
    textAlign: "center",
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
    flexDirection: "column",
    cursor: "pointer",
    borderRadius: "8px",
    width: "50px",
    height: "50px",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    transition: "background-color 0.3s, box-shadow 0.3s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      boxShadow: theme.shadows[3],
    },
  },
  textFieldQuote: {
    flexGrow: 1,
    width: "100%",
    fontSize: 14,
    // borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
  },
  textFieldQuoteNoBorder: {
    flexGrow: 1,
    width: "100%",
    fontSize: 14,
  },
  textFieldQuoteRigt: {
    flexGrow: 1,
    width: "100%",
    fontSize: 14,
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
    borderTop: `1px solid ${theme.palette.cardAdd.borderColor}`,
  },
  profileBottomSetUp: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "300px",
    background: `linear-gradient(to bottom, ${theme.palette.primary.main} 35%, #FFFFFF 15%, #FFFFFF 50%)`,
  },

  profileBottomSetUp23: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
    background: `linear-gradient(to bottom, ${theme.palette.primary.main} 35%, #FFFFFF 15%, #FFFFFF 50%)`,
  },

  profileBottomSetUp23New: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
    background: `linear-gradient(to bottom, ${theme.palette.primary.main} 50%, #FFFFFF 15%, #FFFFFF 50%)`,
  },

  profileBottomSetUp2: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "300px",
    background: `linear-gradient(to bottom, ${theme.palette.primary.main} 35%, #FFFFFF 15%, #FFFFFF 50%)`,
  },
  profileBottomSetUpSmall: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "260px",
    background: `linear-gradient(to bottom, #FFFFFF 42%, #FFFFFF 18%, #FFFFFF 50%)`,
  },

  profileBottomSetUpSmall2: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    height: "50px",
    p: 5,
    background: `linear-gradient(to bottom, #FFFFFF 42%, #FFFFFF 18%, #FFFFFF 50%)`,
  },

  buttonGroupList: {
    display: "flex",
    justifyContent: "center",
    // gap: '10px',
    // marginTop: '16px',
  },

  profileDetailItemList: {
    marginBottom: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  profileDetailItemList2: {
    // marginBottom: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    // gap: "8px",
    color: "#FFFFFF",
    fontWeight: 400,
    fontSize: "19px",
  },

  profileDetailItemListNew: {
    // marginBottom: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    // gap: "8px",
    color: "#FFFFFF",
    fontWeight: 600,
  },
  contactInfoProfile: {
    display: "flex",
    alignItems: "left",
    justifyContent: "left",
    background: theme.palette.primary.main,
    borderRadius: setCardBRadius,
    color: "white",
    fontSize: "15px",
    padding: "2px 10px",
  },
  contactInfoProfileWhiterr: {
    display: "flex",
    alignItems: "left",
    justifyContent: "left",
    // background: theme.palette.primary.main,
    borderRadius: setCardBRadius,
    color: "white",
    fontSize: "15px",
    padding: "2px 10px",
  },
  contactInfoProfile2: {
    display: "flex",
    alignItems: "left",
    justifyContent: "left",
    background: theme.palette.primary.main,
    borderRadius: "4px",
    color: "white",
    padding: "2px 10px",
    textTransform: "uppercase",
    fontSize: "21px",
    fontWeight: 400,
  },

  buttonItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  profileImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "8px",
  },
  profileDetails: {
    color: theme.palette.text.secondary,
    // textAlign: "center",
    marginTop: "10px",
  },
  ActivityHistoryDtails: {
    color: theme.palette.text.secondary,
    textAlign: "center",
    padding: 15,
  },
  profileDetailItem: {
    marginBottom: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    color: theme.palette.cardAdd.headingColor,
    fontWeight: 600,
  },
  profileDetailItem23: {
    marginBottom: "10px",
    display: "flex",
    // justifyContent: "center",
    // alignItems: "center",
    gap: "8px",
    color: theme.palette.cardAdd.headingColor,
    fontWeight: 600,
  },
  contactInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
  },
  contactInfo12: {
    display: "flex",
    // alignItems: "center",
    // justifyContent: "center",
    gap: "16px",
  },
  iconText: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  buttonGroup: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    marginTop: "16px",
  },
  buttonLabel: {
    marginTop: "8px",
  },
  buttonLabelForHeading: {
    marginTop: "2px",
    fontSize: "21px",
  },
 
  editButtonProfileBack: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: "50%",
    color: "#ff5b5b",
    border: "none",
    padding: 5,
    minWidth: 50,
    height: 50,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#ffe6e6",
    "&:hover": {
      backgroundColor: "#ffe6e6",
      "& .MuiSvgIcon-root": {
        color: "#6b3333",
      },
    },
    "&:active": {
      backgroundColor: "#ffe6e6",
      "& .MuiSvgIcon-root": {
        color: "#6b3333",
      },
    },
  },
  editButtonProfileBack2: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "30px",
    fontWeight: 600,
    lineHeight: 1.5,
    borderRadius: "50%",
    color: "#747474",
    border: "none",
    padding: 5,
    minWidth: 60,
    height: 60,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#eee",
    "&:hover": {
      backgroundColor: "#eee",
      "& .MuiSvgIcon-root": {
        color: "#747474",
      },
    },
    "&:active": {
      backgroundColor: "#eee",
      "& .MuiSvgIcon-root": {
        color: "#747474",
      },
    },
  },
  editButtonProfileBox: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 60,
    height: 60,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "#ff6c2f",
    backgroundColor: "#fff0ea",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "0.75rem",
  },
  dashboardPrimary: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.background.default,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  dashboardGreen: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "#007f00",
    backgroundColor: "#cdffcd",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  dashboardGreenWithoutBackColor: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "#007f00",
    // backgroundColor: "#cdffcd",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  dashboardGreenWithoutBackColor2: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "#007f00",
    // backgroundColor: "#cdffcd",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    borderRadius: "50%",
  },
  dashboardGreenWithoutBackColor23: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 40,
    height: 40,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "white",
    backgroundColor: "#007f00",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    borderRadius: "50%",
  },
  dashboard234: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 40,
    height: 40,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "white",
    backgroundColor: "#d30000",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    borderRadius: "50%",
  },
  dashboard2345: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 40,
    height: 40,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "white",
    backgroundColor: "#e1c300",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    borderRadius: "50%",
  },
  dashboardOrangeWithoutBackColor: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "#df6800",
    // backgroundColor: "#cdffcd",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  dashboardLightBlueWithoutBackColor: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "#4eccc5",
    // backgroundColor: "#cdffcd",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  dashboardGreen2: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "white",
    backgroundColor: "#66b266",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  dashboardRed2: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "white",
    backgroundColor: "#e56666",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  dashboardGree2: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "white",
    backgroundColor: "#7ad9d3",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  dashboardRed: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "#d93a3b",
    backgroundColor: "#ffe0e0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  dashboardRedWithoutBackColor: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "#d93a3b",
    // backgroundColor: "#ffe0e0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  dashboardYellow: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "#e1c300",
    // backgroundColor: "#ffe0e0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  dashboardBlue: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "white",
    backgroundColor: "#578fd4",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  dashboardPurpele: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "white",
    backgroundColor: "#957ff2",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  dashboardPurpeleWithoutBackColor: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "#5932ea",
    // backgroundColor: "#957ff2",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  dashboardBlueWithoutBackColor: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 70,
    height: 70,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "#0f5fc2",
    // backgroundColor: "#957ff2",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
  },
  editButtonStatusPaid: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 100,
    height: 30,
    fontWeight: 600,
    lineHeight: 1.5,
    border: "none",
    px: "6px",
    py: "12px",
    color: "#22c55e",
    backgroundColor: "#d3f3df",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: setBRadius,
  },
  editButtonStatusPaidSedul: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 100,
    height: 30,
    fontWeight: 600,
    lineHeight: 1.5,
    border: "none",
    px: "6px",
    py: "12px",
    color: "#22c55e",
    backgroundColor: "#d3f3df",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: setBRadius,
    textAlign: "center",
  },
  editButtonStatusPunchIn: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 100,
    // maxWidth: 100,
    // width: "100%",
    height: 30,
    fontWeight: 600,
    lineHeight: 1.5,
    border: "none",
    px: "6px",
    py: "12px",
    color: "#ff753f",
    backgroundColor: "#ffe2d5",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: setBRadius,
  },
  editButtonStatusPaidPunchDate: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 150,
    height: "100%",
    fontWeight: 600,
    lineHeight: 1.5,
    border: "none",
    padding: "5px",
    px: "6px",
    py: "12px",
    color: "#22c55e",
    backgroundColor: "#d3f3df",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: setBRadius,
    textAlign: "center",
  },
  editButtonStatusPending: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 100,
    // maxWidth: 100,
    // width: "100%",
    height: 30,
    fontWeight: 600,
    lineHeight: 1.5,
    border: "none",
    px: "6px",
    py: "12px",
    color: "#ef5f5f",
    backgroundColor: "#fcdfdf",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: setBRadius,
  },

  editButtonStatusBooked: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 100,
    // maxWidth: 100,
    // width: "100%",
    height: 30,
    fontWeight: 600,
    lineHeight: 1.5,
    border: "none",
    px: "6px",
    py: "12px",
    color: "#db2a2a",
    backgroundColor: "#ffe0e0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: setBRadius,
  },
  editButtonStatusAvailable: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 100,
    // maxWidth: 100,
    // width: "100%",
    height: 30,
    fontWeight: 600,
    lineHeight: 1.5,
    border: "none",
    px: "6px",
    py: "12px",
    color: "#007f00",
    backgroundColor: "#cdffcd",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: setBRadius,
  },

  editButtonStatusAvailableView: {
    y: "0.5rem",
    fontSize: "0.875rem",
    // width: "100%",
    // maxWidth: 100,
    // width: "100%",
    minHeight: 30,
    fontWeight: 600,
    lineHeight: 1.5,
    border: "none",
    px: "6px",
    py: "12px",
    color: "#007f00",
    backgroundColor: "#cdffcd",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "20px",
  },
  editButtonStatusAvailableView3: {
    y: "0.5rem",
    fontSize: "1rem",
    // width: "100%",
    // maxWidth: 100,
    // width: "100%",
    minHeight: 35,
    fontWeight: 600,
    lineHeight: 1.5,
    border: "none",
    px: "6px",
    py: "12px",
    color: "black",
    backgroundColor: "white",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "20px",
  },

  editButtonStatusAvailableView2: {
    y: "0.5rem",
    fontSize: "0.875rem",
    // width: "100%",
    // maxWidth: 100,
    // width: "100%",
    minHeight: 30,
    fontWeight: 600,
    lineHeight: 1.5,
    border: "none",
    px: "6px",
    py: "12px",
    color: "white",
    backgroundColor: "#3f9b59",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "20px",
  },

  editButtonStatusAvailablenotwidth: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 80,
    // maxWidth: 100,
    // width: "100%",
    height: 30,
    fontWeight: 600,
    lineHeight: 1.5,
    border: "none",
    px: "6px",
    py: "12px",
    color: "#007f00",
    backgroundColor: "#cdffcd",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: setBRadius,
  },

  editButtonStatusWaiting: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 100,
    // maxWidth: 100,
    // width: "100%",
    height: 30,
    fontWeight: 600,
    lineHeight: 1.5,
    border: "none",
    px: "6px",
    py: "12px",
    color: "#e3791a",
    backgroundColor: "#fff1cd",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: setBRadius,
  },
  editButtonPayment: {
    y: "0.5rem",
    fontSize: "1.2rem",
    minWidth: 150,
    // maxWidth: 100,
    // width: "100%",
    minHeight: 40,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    px: "6px",
    py: "12px",
    color: "white",
    backgroundColor: "#4eccc5",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: setBRadius,
  },

  staffNameBox: {
    y: "0.5rem",
    fontSize: "12px",
    minHeight: 40,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    color: "white",
    backgroundColor: "#4182f9",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "20px",
  },

  editButtonProfileBoxCursour: {
    y: "0.5rem",
    fontSize: "0.875rem",
    width: 60,
    height: 60,
    fontWeight: 400,
    lineHeight: 1.5,
    border: "none",
    padding: 1,
    color: "#ff6c2f",
    backgroundColor: "#ffccbc",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "0.75rem",
    cursor: "pointer",
  },
  profileBottomDwonProfile: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    width: "240px",
    height: "100px",
    borderRadius: "8px",
    backgroundColor: "#000000",
    // border: `5px solid #FFFFFF`,
    overflow: "hidden",
    // marginTop: '-25px',
    zIndex: 1,
  },
  profileTypograpy: {
    fontSize: "45px",
    // color: "#FFFFFF",
    fontWeight: 600,
  },
  profileTypograpy2: {
    fontSize: "45px",
    // color: "#FFFFFF",
    fontWeight: 600,
  },
  showMoreLink: {
    color: "#1976d2",
    cursor: "pointer",
    textDecoration: "underline",
    "&:hover": {
      textDecoration: "none",
    },
  },
  tableHeaderCell: {
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
    // add any other styling you need here
  },
  profileBottomDwon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    width: "100px",
    height: "100px",
    borderRadius: "50%",
    backgroundColor: "#FFFFFF",
    border: `5px solid #FFFFFF`,
    overflow: "hidden",
    // marginTop: '-25px',
    zIndex: 1,
  },
  profileBottomDwonNew: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    // minWidth: "100px",
    // minHeight: "100px",
    borderRadius: 4,
    // backgroundColor: "#FFFFFF",
    // border: `1px solid #FFFFFF`,
    overflow: "hidden",
    // marginTop: '-25px',
    // zIndex: 1,
  },
  profileBottomDwon23: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    width: "100px",
    height: "100px",
    borderRadius: "50%",
    backgroundColor: "#FFFFFF",
    // border: `5px solid #FFFFFF`,
    overflow: "hidden",
    // marginTop: '-25px',
    zIndex: 1,
  },
  // availableBox: {
  //   backgroundColor: '#d3f3df',
  //   color: '#22c55e',
  //   fontSize: "14px",
  //   fontWeight: 600,
  //   height: "21px",
  //   px: "6px",
  //   py: "12px",
  //   textAlign: "center",
  //   borderRadius: "4px"
  // },

  availableBox: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    lineHeight: 1.5,
    // backgroundColor: "#ff6c2f",
    borderRadius: 4,
    // boxShadow: "0px 2px 6px 0px",
    // color: '#aab8c5',
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      borderColor: "transparent",
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  bookedBox: {
    backgroundColor: "#B0BEC5",
    color: "white",
    fontWeight: "bold",
    pointerEvents: "none",
  },

  cardTitleGrid: {
    // borderColor: appColor.greenSmoke[20],
  },

  cardTitleTypography: {
    display: "flex",
    alignItems: "center",
    fontWeight: 500,
    fontSize: "21px",
    // color: appColor.black,
  },

  typography: {
    fontWeight: 400,
    fontSize: 14,
  },

  searchButton: {
    // backgroundColor: appColor.blue[100],
    textTransform: "none",
    boxShadow: "none",
    "&:hover": {
      // backgroundColor: appColor.blue[100],
      boxShadow: "none",
    },
    "&:active": {
      // backgroundColor: appColor.blue[100],
      boxShadow: "none",
    },
  },
  SelectImageTableBoxwithBorder: {
    width: "56px",
    height: "56px",
    p: "5px",
    // border: "1px solid",
    borderColor: theme.palette.cardAdd.borderColor,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  SelectImageTableBoxwithBorder2: {
    width: "100%",
    aspectRatio: "1 / 0.5",
    padding: "5px",
    backgroundColor: "#f5f5f56b",
    borderColor: "",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  SelectImageTableBoxwithBorder24: {
    width: "100%",
    aspectRatio: "1 / 0.5",
  },
  isLoading: {
    color: theme.palette.primary.main,
    // width: '12px',
    // height: '12px',
  },

  quotesCardInvoice: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    [theme.breakpoints.down("md")]: {
      marginLeft: "1px",
      marginRight: "1px",
    },
    marginLeft: "2px",
    marginRight: "2px",
    padding: "2px",
    borderRadius: "8px",
    boxShadow: "none",
  },

  quotesCardStaff: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    [theme.breakpoints.down("md")]: {
      marginLeft: "2px",
      marginRight: "2px",
    },
    marginLeft: "5px",
    marginRight: "5px",
    // padding: "24px",
    // borderRadius: "12px",
    boxShadow: "none",
  },
  ListPayroll: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    alignItems: "center",
    // border: `1px solid #c4c4c4`,
    width: "100%",
    height: "100%",
    borderRadius: setBRadius,
    // height: 70
    size: "small",
    backgroundColor: theme.palette.cardAdd.secBackColor,
  },
  ListItemPayroll: {
    // borderLeft: `1px solid ${theme.palette.cardAdd.borderColor}`,
    margin: 0,
    padding: 0,
    display: "flex",
    alignItems: "center",
    height: 32,
  },
  ListItemPayrollToday: {
    fontWeight: "normal",
    // minWidth: "32px",
    height: "32px",
    padding: "0",
    margin: 0,
    boxShadow: "none",
    // border: `1px solid ${theme.palette.cardAdd.borderColor}`,
    backgroundColor: theme.palette.cardAdd.secBackColor,
    color: theme.palette.primary.main,
    textTransform: "uppercase",
    // "&:hover": {
    //   backgroundColor: theme.palette.primary.dark,
    // },
  },
  quotesCard: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    [theme.breakpoints.down("md")]: {
      marginLeft: "2px",
      marginRight: "2px",
    },
    marginLeft: "5px",
    marginRight: "5px",
    padding: "24px",
    // borderRadius: "12px",
    boxShadow: "none",
    minHeight: "100vh",
  },

  quotesCard2: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    [theme.breakpoints.down("md")]: {
      marginLeft: "2px",
      marginRight: "2px",
    },
    marginLeft: "5px",
    marginRight: "5px",
    padding: "24px",
    // borderRadius: "12px",
    boxShadow: "none",
  },
  OrderLisViewCard: {
    backgroundColor: "#1b344e",
    [theme.breakpoints.down("md")]: {
      marginLeft: "12px",
      marginRight: "12px",
    },
    marginLeft: "98px",
    marginRight: "98px",
    padding: "24px",
    // borderRadius: "12px",
    borderRadius: "none",
    boxShadow: "none",
    minHeight: "100vh",
  },
  h4Quoto: {
    fontSize: "1.125rem",
    fontWeight: 600,
    lineHeight: "21px",
    color: theme.palette.heading.headingColor,
  },
  h4QuotoTopBar: {
    fontSize: "1.125rem",
    fontWeight: 600,
    lineHeight: "21px",
    color: theme.palette.topbarButton.topBarHeading,
  },
  h4QuotoTopBarSearch: {
    fontSize: "1.125rem",
    fontWeight: 600,
    lineHeight: "21px",
    color: theme.palette.topbarButton.topBarHeading,
  },
  h4QuotoImg: {
    fontSize: "1.125rem",
    fontWeight: 400,
    lineHeight: "21px",
    color: theme.palette.topbarButton.drawerHeadingText,
  },
  titleGridInvoice: {
    borderColor: theme.palette.cardAdd.borderColor,
    borderRadius: "12px",
    // backgroundColor: "#dcf4f3",
    // padding: "2px"
  },
  invoiceImg: {
    width: "100%",
    height: "50px",
    padding: "12px",
    borderRadius: "12px",
    display: "block",
    cursor: "pointer",
  },
  bodyPrintHeading: {
    fontSize: "13px",
    lineHeight: 1.2,
    // marginBottom: "1rem",
    color: "black",
    // fontWeight: "600px",
    fontWeight: 400,
    margin: 0,
    textTransform: "uppercase",
  },
  tablheader200: {
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
    borderTop: `1px solid ${theme.palette.cardAdd.borderColor}`,
    minWidth: "200px",
    color: "#e0e1e5",
  },
  tablheader150: {
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
    borderTop: `1px solid ${theme.palette.cardAdd.borderColor}`,
    minWidth: "150px",
    color: "#e0e1e5",
  },
  tablheader250: {
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
    borderTop: `1px solid ${theme.palette.cardAdd.borderColor}`,
    minWidth: "250px",
    color: "#e0e1e5",
  },
  tablheader50: {
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
    borderTop: `1px solid ${theme.palette.cardAdd.borderColor}`,
    minWidth: "50px",
    color: "#e0e1e5",
  },
  tablheader100: {
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
    borderTop: `1px solid ${theme.palette.cardAdd.borderColor}`,
    minWidth: "100px",
    color: "#e0e1e5",
  },
  tablheader300: {
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
    borderTop: `1px solid ${theme.palette.cardAdd.borderColor}`,
    minWidth: "300px",
    color: "#e0e1e5",
  },
  cardPaymenyt: {
    backgroundColor: "#ffe2d5",
    p: "24px",
    borderRadius: "8px",
    boxShadow: "none",
  },
  cardPaymenytSecound: {
    backgroundColor: "#dcf4f3",
    p: "24px",
    borderRadius: "8px",
    boxShadow: "none",
  },
  iconPaymenyt: {
    color: "#ff6c2f",
    fontSize: 60,
  },
  iconPaymenytSec: {
    color: "#346c6a",
    fontSize: 60,
  },
  statusCount: {
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "1rem",
    margin: "8px 0",
  },
  paymentRight: {
    borderRight: `3px solid ${theme.palette.cardAdd.borderColor}`,
  },
  paymentBox: {
    borderRight: `3px solid ${theme.palette.cardAdd.borderColor}`,
  },
  h3HeadingBox: {
    fontWeight: 700,
    lineHeight: "38.5px",
    color: "#313b5e",
    fontSize: "40px",
    pb: 1,
  },
  // cardTitleTypographPayment: {
  //   display: "flex",
  //   alignItems: "center",
  //   fontWeight: 500,
  //   fontSize: "18px",
  //   textTransform: "uppercase",
  // },
  cardTitleTypographPayment: {
    fontSize: "1.125rem",
    fontWeight: 600,
    lineHeight: "21px",
    color: theme.palette.heading.headingColor,
    textTransform: "uppercase",
  },
  h3HeadingBoxSec: {
    fontWeight: 400,
    lineHeight: "38.5px",
    color: "#313b5e",
    fontSize: "21px",
    pb: 1,
  },
  SelectImagInvoiceBox: {
    width: "80px",
    height: "80px",
    p: "5px",
    border: "1px solid",
    borderColor: theme.palette.cardAdd.borderColor,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  SelectImageInvoice: {
    width: "70px",
    height: "70px",
    border: "none",
    position: "inherit",
  },
  cardTitleTypographPaymentSec: {
    fontSize: "1.125rem",
    fontWeight: 700,
    lineHeight: "13px",
    color: theme.palette.heading.headingColor,
    textTransform: "uppercase",
  },
  dropdownPaper: {
    maxHeight: "200px",
    overflowY: "auto",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  arrowIcon: {
    fontSize: "30px",
  },
  SelectImageTable12: {
    width: "90%",
    height: "90%",
    border: "none",
    position: "inherit",
  },
  SelectImageTableBoxwithBorder245: {
    width: "100%",
    aspectRatio: "1 /0.7",
    padding: "5px",
    // backgroundColor: "#f5f5f56b",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },

  SelectImageTableBoxwithBorder2456: {
    width: "100%",
    aspectRatio: "1 /0.7",
    padding: "5px",
    backgroundColor: "#f5f5f56b",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },

  requiedfield: {
    color: "red",
    // fontSize: "15px"
  },

  orderListLeftCard: {
    boxShadow: "none",
    backgroundColor: theme.palette.orderListProp.leftBgColor || "#776c7b",
    borderRadius: orderListButtoCardBRadius,
    fontSize: "16px",
    p: 5,
    fontWeight: 400,
    minHeight: "100vh",
  },

  orderListLeftHeader: {
    borderColor: theme.palette.orderListProp.lable2buttonbgColor || "#e8f0f3",
    backgroundColor: theme.palette.orderListProp.lable1BgColor || "#ffffff",
  },

  ordeListButton: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    lineHeight: 1.5,
    backgroundColor: theme.palette.orderListProp.lable2FontColor || "#eaf3f2",
    borderRadius: orderListButtonBRadius,
    // boxShadow: "0px 2px 6px 0px",
    // color: '#aab8c5',
    color: theme.palette.orderListProp.lable1buttonIconColor || "#599d95",
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      borderColor: "transparent",
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  orderListTextField: {
    width: "100%",
    backgroundColor: "white",
    borderRadius: setBRadius,
    "& .MuiOutlinedInput-root": {
      borderRadius: orderListButtonBRadius,
      color: theme.palette.orderListProp.lable1buttonIconColor || "#599d95",
      backgroundColor: theme.palette.orderListProp.lable2FontColor || "#eaf3f2",
      "& .MuiInputBase-input": {
        color: theme.palette.orderListProp.lable1buttonIconColor || "#599d95",
      },
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
  },

  orderListSearchWidgetIcon: {
    position: "absolute",
    zIndex: 10,
    fontSize: "25px",
    left: "11px",
    top: "50%",
    // color: theme.palette.orderListProp.lable1buttonIconColor || "#599d95",
    transform: "translateY(-50%)",
    cursor: "pointer",
  },
  orderListRightCard: {
    boxShadow: "none",
    backgroundColor: theme.palette.orderListProp.lable2BgColor || "#ffffff",
    borderRadius: orderListButtoCardBRadius,
    fontSize: "16px",
    p: 5,
    fontWeight: 400,
    minHeight: "100vh",
  },
  orderListRightCard2: {
    boxShadow: "none",
    backgroundColor: theme.palette.orderListProp.lable2BgColor || "#ffffff",
    borderRadius: orderListButtoCardBRadius,
    fontSize: "16px",
    p: 5,
    fontWeight: 400,
  },

  orderListRightHeader: {
    borderColor: theme.palette.cardAdd.borderColor,
    backgroundColor: theme.palette.orderListProp.lable1BgColor || "#ffffff",
  },

  orderListRightHeader2: {
    borderColor: theme.palette.orderListProp.lable2buttonbgColor || "#e8f0f3",
  },

  orderListTabButtonSelect: {
    color: theme.palette.orderListProp.payButtonColor || "#298076",
    y: "0.5rem",
    fontSize: "15px",
    minHeight: 35,
    fontWeight: 600,
    lineHeight: 1.5,
    border: "none",
    px: "3px",
    py: "6px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: orderListButtonBRadius,
  },
  orderListTabButton: {
    color: theme.palette.orderListProp.lable1buttonbgColor || "#85858f",
    y: "0.5rem",
    fontSize: "15px",
    minHeight: 35,
    fontWeight: 600,
    lineHeight: 1.5,
    border: "none",
    px: "3px",
    py: "6px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: orderListButtonBRadius,
  },
  tabTypograpy: {
    fontSize: "15px",
    fontWeight: 600,
  },
  orderListProductCard: {
    boxShadow: "none",
    backgroundColor:
      theme.palette.orderListProp.lable2buttonIconColor || "#eaf3f2",
    borderRadius: orderListButtoCardBRadius,
    fontSize: "12px",
    p: 5,
    fontWeight: 400,
  },
  productCardTypograpy: {
    color: theme.palette.orderListProp.lable1buttonbgColor || "#85858f",
    fontWeight: 600,
    margin: 0,
    fontSize: "13px",
  },
  orderLsitHeadingTypo: {
    fontSize: orderListHeadingFontSize,
    fontWeight: 600,
    margin: 0,
    color: theme.palette.orderListProp.headingColor || "#639182",
  },
  orTableHeaderRow: {
    textTransform: "uppercase",
    background: theme.palette.orderListProp.lable1FontColor || "#ffffff",
    color: theme.palette.orderListProp.headingColor || "#639182",
    width: "100%",
  },
  orTableContainer: {
    width: "100%",
  },
  oLRightHeader: {
    borderColor: theme.palette.orderListProp.lable2buttonbgColor || "#e8f0f3",
    background: theme.palette.orderListProp.lable1FontColor || "#ffffff",
  },
  oLTablHeader: {
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
    borderTop: `1px solid ${theme.palette.cardAdd.borderColor}`,
    color: theme.palette.orderListProp.payButtonColor || "#298076",
  },
  leftORTypo: {
    fontSize: "13px",
    color: theme.palette.orderListProp.lable1buttonbgColor || "#85858f",
  },
  oLCardTitle: {
    fontSize: "16px",
    fontWeight: 600,
    margin: 0,
    color: theme.palette.orderListProp.payButtonColor || "#c8f7a1",
    textTransform: "uppercase",
    // color: "#bdbec7",
  },
  orderListAddButton: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    lineHeight: 1.5,
    backgroundColor: theme.palette.orderListProp.payButtonColor || "#c8f7a1",
    borderRadius: orderListButtonBRadius,
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      borderColor: "transparent",
      backgroundColor: theme.palette.orderListProp.payButtonColor || "#c8f7a1",

      opacity: 0.65,
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  orderListCancelButton: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    lineHeight: 1.5,
    color: theme.palette.orderListProp.payButtonColor || "#c8f7a1",

    // backgroundColor: "#ff6c2f",
    borderRadius: orderListButtonBRadius,
    // boxShadow: "0px 2px 6px 0px",
    // color: '#aab8c5',
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      borderColor: "transparent",
      // backgroundColor: theme.palette.orderListProp.payButtonColor || "#c8f7a1",

      // opacity: 0.65,
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  oLQty: {
    width: "100%",
    backgroundColor: "white",
    // backgroundColor: theme.palette.cardAdd.secBackColor,
    borderRadius: orderListButtonBRadius,
    "& .MuiOutlinedInput-root": {
      borderRadius: orderListButtonBRadius,
      color: theme.palette.orderListProp.lable1buttonbgColor || "#85858f",
      backgroundColor: theme.palette.orderListProp.lable2BgColor,
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
  },
  oLQtyIconButton: {
    width: 20,
    height: 20,
    backgroundColor: theme.palette.orderListProp.headingColor || "#c8f7a1",
    cursor: "pointer",
    borderRadius: orderListButtonBRadius,
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      borderColor: "transparent",
      backgroundColor: theme.palette.orderListProp.payButtonColor || "#c8f7a1",
      opacity: 0.65,
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  oLDeleteButton: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: 1.5,
    color: theme.palette.iconButton.deleteButtonColor,
    border: "none",
    padding: 1,
    minWidth: 44,
    height: 33,
    display: "flex",
    justifyContent: "center",
    backgroundColor: theme.palette.orderListProp.lable2BgColor || "#ffffff",
    borderRadius: orderListButtoCardBRadius,
    alignItems: "center",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      "& .MuiSvgIcon-root": {
        color: theme.palette.iconButton.deleteButtonColor,
      },
    },
    boxShadow: "none",
  },
  oLEditListIcon: {
    fontSize: "21px",
    color: theme.palette.iconButton.deleteButtonColor,
  },
  oLTextField: {
    flexGrow: 1,
    width: "100%",
    fontSize: "13px",
    color: theme.palette.orderListProp.lable1buttonbgColor || "#85858f",
  },
  oLConformButton: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    fontWeight: 600,
    lineHeight: 1.5,
    textTransform: "uppercase",
    width: "100%",
    height: "45px",
    backgroundColor: theme.palette.orderListProp.payButtonColor || "#c8f7a1",
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      borderColor: "transparent",
      backgroundColor: theme.palette.orderListProp.payButtonColor || "#c8f7a1",
      opacity: 0.5,
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  oLOCancelButton: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    fontWeight: 600,
    lineHeight: 1.5,
    textTransform: "uppercase",
    width: "100%",
    height: "45px",
    border: "1px solid",
    borderColor: theme.palette.orderListProp.payButtonColor || "#c8f7a1",
    color: theme.palette.orderListProp.payButtonColor || "#c8f7a1",
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      borderColor: "transparent",
      color: theme.palette.orderListProp.payButtonColor || "#c8f7a1",
      opacity: 0.5,
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  OLbody2: {
    fontSize: "0.875rem",
    lineHeight: 1.13,
    marginBottom: "1rem",
    fontWeight: 600,
    color: theme.palette.orderListProp.lable1buttonbgColor || "#85858f",
  },

  //-------------------------------clind management ---------------------------------------------------------------------

  formControlList2: {
    border: "none",
    // paddingLeft: "15px",
    // paddingRight: "15px",
    backgroundColor: theme.palette.primary.contrastText,
    boxShadow: "none",
    height: "34px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  h4Profile: {
    fontSize: "1.05rem",
    fontWeight: 550,
    lineHeight: "21px",
    color: theme.palette.heading.headingColor,
  },
  headingSummary: {
    fontSize: "18px",
    fontWeight: 500,
    lineHeight: 1.1,
    color: theme.palette.heading.headingColor,
  },
  bodyPrint1: {
    fontSize: "12px",
    lineHeight: 1.2,
    // marginBottom: "1rem",
    color: "black",
    fontWeight: 400,
    margin: 0,
    // textTransform: "uppercase",
  },

  bodyPrint1333: {
    fontSize: "12px",
    lineHeight: 0.4,
    // marginBottom: "1rem",
    color: "black",
    fontWeight: 400,
    margin: 0,
    // textTransform: "uppercase",
  },

  bodyPrint133: {
    fontSize: "12px",
    // lineHeight: 1.5,
    // marginBottom: "1rem",
    color: "black",
    fontWeight: 400,
    margin: 0,
    // textTransform: "uppercase",
  },
  cardNonRedius: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    p: "24px",
    // borderRadius: "none",
    boxShadow: "none",
  },
  cardProfileView: {
    backgroundColor: theme.palette.cardAdd.thirdBgColor,
    p: "24px",
    borderRadius: "5px",
    boxShadow: "none",
  },
  addButtonProfile: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 400,
    lineHeight: 1.5,
    backgroundColor: "#06b57d",
    // backgroundColor: "#ff6c2f",
    borderRadius: 4,
    // boxShadow: "0px 2px 6px 0px",
    // color: '#aab8c5',
    fontSize: "15px",
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      borderColor: "transparent",
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  tableHeaderRowProfile: {
    background: "#979797",
    // borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
  },
  tablheaderSelect: {
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
    color: "white",
    // fontSize: "18px"
  },
  tablheaderProfile: {
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
    borderTop: `1px solid ${theme.palette.cardAdd.borderColor}`,
    color: theme.palette.primary.contrastText,
  },
  tableReport: {
    width: "100%",
    marginBottom: "1.5rem",
    verticalAlign: "top",
    // borderColor: `1px solid ${theme.palette.cardAdd.borderColor}`,
    "& > :not(caption) > * > *": {
      padding: "0.85rem 0.85rem",
    },
  },

  tableHeaderRowReport: {
    // background: "#dadee5",
    // borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
    padding: "none",
  },
  tablheaderReport: {
    border: `1px solid #767c83`,
    // borderTop: `1px solid ${theme.palette.cardAdd.borderColor}`,
    padding: "none",
  },
  tablCellReport: {
    border: `1px solid #767c83`,
    padding: "none",
  },
  tableRowReport: {
    padding: "none",
  },
  editButtonListProfile: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: "8px",
    // opacity: 0.65,
    color: theme.palette.iconButton.editIconColor,
    border: "none",
    padding: 1,
    minWidth: 32,
    height: 25,
    display: "flex",
    justifyContent: "center",
    backgroundColor: theme.palette.iconButton.editButtonColor,
    alignItems: "center",
    "&:hover": {
      backgroundColor: theme.palette.iconButton.editIconColor,
      "& .MuiSvgIcon-root": {
        color: theme.palette.iconButton.editButtonColor,
      },
    },
    boxShadow: "none",
  },
  deleteButtonListProfilepage: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: "8px",
    // opacity: 0.65,
    color: theme.palette.iconButton.deleteIconColor,
    border: "none",
    padding: 1,
    minWidth: 32,
    height: 25,
    display: "flex",
    justifyContent: "center",
    backgroundColor: theme.palette.iconButton.deleteButtonColor,
    alignItems: "center",
    "&:hover": {
      backgroundColor: theme.palette.iconButton.deleteIconColor,
      "& .MuiSvgIcon-root": {
        color: theme.palette.iconButton.deleteButtonColor,
      },
    },
    boxShadow: "none",
  },

  editButtonListTwo: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: "8px",
    opacity: 0.65,
    color: "#ff6c2f1a",
    border: "none",
    padding: 1,
    minWidth: 65,
    height: 33,
    display: "flex",
    justifyContent: "center",
    backgroundColor: "#4caf50",
    alignItems: "center",
    "&:hover": {
      color: "white", // Ensures button text turns white
      backgroundColor: "#7de88a",
      "& .MuiTypography-root": {
        color: "white", // Ensures Typography inside button turns white
      },
      "& .MuiSvgIcon-root": {
        color: "white", // Ensures icons inside button turn white
      },
    },
    boxShadow: "none",
  },
  primaryViewButtonList: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: "8px",
    border: "none",
    padding: 1,
    minWidth: 44,
    height: 33,
    display: "flex",
    justifyContent: "center",

    alignItems: "center",

    boxShadow: "none",
  },
  deleteButtonListProfile: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: "8px",
    opacity: 0.65,
    color: "#ef5f5f",
    border: "none",
    padding: 1,
    minWidth: 44,
    height: 33,
    display: "flex",
    justifyContent: "center",
    backgroundColor: "#f44336",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#ef5f5f",
      "& .MuiSvgIcon-root": {
        color: "white",
      },
    },
    boxShadow: "none",
  },
  editListIconProfile: {
    fontSize: "21px",
    color: "white",
  },
  editListIconProfileAddColor: {
    fontSize: "18px",
  },
  editListIconTwo: {
    fontSize: "11px",
    color: "white",
  },
  ActivityHistoryHEadingWhite: {
    fontSize: "15px",
    margin: 0,
    // color: theme.palette.cardAdd.headingColor,
    // textTransform: "uppercase",
    color: "white",
  },
  ActivityHistoryHEadingEmail: {
    fontSize: "15px",
    margin: 0,
    color: theme.palette.cardAdd.headingColor,
    // textTransform: "uppercase",
  },
  ActivityHistoryHEadingWhite2: {
    fontSize: "15px",
    margin: 0,
    // color: theme.palette.cardAdd.headingColor,
    // textTransform: "uppercase",
    color: theme.palette.primary.main,
  },
  SelectImageTableCompanyLogo: {
    width: "150px",
    height: "50px",
    border: "none",
    position: "inherit",
  },
  SelectImageTableBoxCompanyLogo: {
    width: "150px",
    height: "50px",
    p: "5px",
    // border: "1px solid",
    // borderColor: theme.palette.cardAdd.borderColor,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  profileBottomDwon2: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    maxWidth: "120px",
    maxHeight: "120px",
    minWidth: "60px",
    minHeight: "60px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    // border: `5px solid #FFFFFF`,
    overflow: "hidden",
    // marginTop: '-25px',
    zIndex: 1,
    aspectRatio: "1 / 0.75",
  },
  profileBottomDwonSelect: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    width: "120px",
    height: "120px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    // border: `5px solid #FFFFFF`,
    overflow: "hidden",
    // marginTop: '-25px',
    zIndex: 1,
  },
  profileBottomDwonReport: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    width: "100px",
    height: "100px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    // border: `5px solid #FFFFFF`,
    overflow: "hidden",
    // marginTop: '-25px',
    zIndex: 1,
  },
  contactInfoViewPage: {
    display: "flex",
    alignItems: "left",
    justifyContent: "left",
    // gap: "16px",
    fontWeight: 500,
    fontSize: "17px",
  },
  text12: {
    // color: "#003c8a",
    fontWeight: 600,
    fontSize: "15px",
    textDecoration: "none",
  },
  text123: {
    // color: "#003c8a",
    fontWeight: 500,
    fontSize: "15px",
    textDecoration: "none",
  },
  text2: {
    color: "#7c7c7c",
    fontWeight: 500,
    fontSize: "15px",
    textDecoration: "none",
  },
  text23: {
    color: "#7c7c7c",
    fontWeight: 500,
    fontSize: "15px",
    textDecoration: "none",
  },
  contactInfoProfileWhite: {
    display: "flex",
    alignItems: "left",
    justifyContent: "left",
    gap: "16px",
    background: "white",
    borderRadius: "4px",
    paddingTop: "4px",
    paddingBottom: "4px",
    color: theme.palette.cardAdd.headingColor,
    paddingLeft: "16px",
    paddingRight: "16px",
    fontWeight: 500,
    fontSize: "15px",
    textDecoration: "none",
  },
  profileBottomReport: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "250px",
    // background: `linear-gradient(to bottom, ${theme.palette.text.primary} 42%, #FFFFFF 18%, #FFFFFF 50%)`,
    background: `linear-gradient(to bottom, #efefef 42%, #FFFFFF 18%, #FFFFFF 50%)`,
  },
  editButtonProfileSummary: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
    opacity: 0.65,
    borderRadius: "8px",
    color: "#FFFFFF",
    border: "none",
    padding: 1,
    minWidth: 44,
    height: 33,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
      "& .MuiSvgIcon-root": {
        color: "white",
      },
    },
    "&:active": {
      backgroundColor: "#ff6c2f",
      "& .MuiSvgIcon-root": {
        color: "white",
      },
    },
  },
  textFieldQuoteButtonIcon: {
    fontSize: 38,
  },
  selectedButton: {
    width: "100%",
    backgroundColor: "#bec2c6",
    color: "#eef2f7",
    fontSize: "14px",
    borderRadius: "8px",
    textTransform: "none",
  },
  isNotSelectedButton: {
    width: "100%",
    backgroundColor: "#eef2f7",
    color: "#4a5151",
    fontSize: "17px",
    borderRadius: "8px",
    textTransform: "none",
  },
  titleSummaryReportBox: {
    border: `1px solid ${theme.palette.cardAdd.borderColor}`,
    borderRadius: "4px",
    backgroundColor: "#ffff",
    padding: "5px",
  },
  policySummaryBox: {
    border: `1px solid ${theme.palette.cardAdd.borderColor}`,
    borderRadius: "4px",
    backgroundColor: "#dadee5",
    padding: "2px",
  },
  policySummaryTitile: {
    color: "black",
    fontSize: "17px",
    fontWeight: 500,
  },
  titleGridBottomBoredr2: {
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
  },

  grid1: {
    mt: 2,
    maxHeight: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  cancelButton: {
    backgroundColor: "#b71c1c",
    textTransform: "none",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#b71c1c",
      boxShadow: "none",
    },
    "&:active": {
      backgroundColor: "#b71c1c",
      boxShadow: "none",
    },
  },

  saveButton: {
    backgroundColor: "green",
    textTransform: "none",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "green",
      boxShadow: "none",
    },
    "&:active": {
      backgroundColor: "green",
      boxShadow: "none",
    },
  },

  /* The ToDo screen's view toggle. It painted the CSS keyword `green` — not the
     palette's success colour, not any GT colour, and unreachable from Setting →
     Theme Color. It is the page's main action, so it takes the primary role
     like every other one. */
  indexButton: {
    textTransform: "none",
    boxShadow: "none",
    fontSize: 16,
    minHeight: control.md,
    borderRadius: setBRadius,
    "& .MuiSvgIcon-root": {
      fontSize: 26,
    },
    ...buttonRoleSx(theme, "primary", true),
    "&:focus-visible": {
      outline: `2px solid ${alpha(theme.palette.primary.main, 0.55)}`,
      outlineOffset: 2,
    },
  },

  indexBox: {
    backgroundColor: "#e0e0e0",
    minHeight: "100vh",
    width: "100%",
  },

  outlierBox: {
    backgroundColor: "#e0e0e0",
    p: 2,
    width: "100%",
  },

  tableContainer: {
    borderRadius: 10,
  },

  tableCell: {
    fontSize: "16px",
    fontWeight: 600,
  },

  actionsButton: {
    display: "flex",
    justifyContent: "center",
    border: "1px solid green",
    borderRadius: 8,
    cursor: "pointer",
    mr: 0.5,
    p: 0.5,
    minWidth: "45px",
    alignItems: "center",
    color: "green",
  },

  backButton: {
    display: "flex",
    justifyContent: "center",
    // border: "1px solid green",
    // borderRadius: 8,
    cursor: "pointer",
    mr: 0.5,
    p: 0.5,
    minWidth: "45px",
    alignItems: "center",
    color: "green",
  },

  tablePagination: {
    "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows, .MuiTablePagination-select":
      {
        fontSize: 14,
      },
  },

  boxImg: {
    border: "1px solid #d3d3d3",
    borderRadius: "4px",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "8px",
    width: "100%",
  },

  image: {
    maxWidth: "200px",
    maxHeight: "200px",
    marginRight: "auto",
  },

  boxColor: {
    border: "1px solid #d3d3d3",
    borderRadius: "4px",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: "8px",
  },

  boxColorWithin: {
    width: "50px",
    height: "50px",
    backgroundColor: "#ffffff",
    marginLeft: "8px",
    border: "1px solid #d3d3d3",
  },

  cardTitleProfileVieTypography: {
    display: "flex",
    alignItems: "center",
    fontWeight: 500,
    fontSize: "24px",
    // color: appColor.black,
  },

  CustomerViewtypography: {
    fontWeight: 400,
    fontSize: 16,
  },

  resetButton: {
    // backgroundColor: appColor.grey[90],
    textTransform: "none",
    boxShadow: "none",
    "&:hover": {
      // backgroundColor: appColor.grey[90],
      boxShadow: "none",
    },
    "&:active": {
      // backgroundColor: appColor.grey[90],
      boxShadow: "none",
    },
  },

  tableHeaderCellRight: {
    fontSize: "1.1rem",
    // color: appColor.white,
    textAlign: "right",
  },

  /**
   * MUI DataGrid, brought onto the design system.
   *
   * Client List, Policy List and several report screens are DataGrids rather
   * than hand-built tables, so this class is the only way to give them the same
   * density, header band, hover and focus treatment as the rest of the product.
   * Column definitions, sorting, selection and paging are untouched.
   */
  dataGridTable: {
    border: "none",
    borderRadius: 0,
    color: theme.palette.text.secondary,
    fontSize: "13.5px",
    "--DataGrid-rowBorderColor": alpha(theme.palette.divider, 0.7),

    "& .MuiDataGrid-columnHeaders": {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiDataGrid-columnHeader": {
      textTransform: "uppercase",
      color: theme.palette.text.primary,
      fontSize: "11.5px",
      fontWeight: 700,
      letterSpacing: "0.06em",
      backgroundColor: theme.palette.cardAdd.secBackColor,
      borderTop: "none",
      "&:focus, &:focus-within": {
        outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
        outlineOffset: -2,
      },
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 700,
      letterSpacing: "0.06em",
    },
    "& .MuiDataGrid-columnSeparator": {
      color: alpha(theme.palette.divider, 0.8),
    },

    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
      paddingLeft: "16px",
      paddingRight: "16px",
      "&:focus, &:focus-within": {
        outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
        outlineOffset: -2,
      },
    },
    "& .MuiDataGrid-row": {
      transition: transition(["background-color"], motion.fast),
      "&:hover": {
        backgroundColor: theme.palette.cardAdd.tablehover,
      },
      "&.Mui-selected": {
        backgroundColor: alpha(theme.palette.primary.main, 0.07),
        "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
      },
    },

    /* The grid owns its own horizontal scrolling, which is what keeps a wide
       client table from giving the whole page a scrollbar. */
    "& .MuiDataGrid-virtualScroller": {
      ...compactScrollbar(theme),
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiDataGrid-overlay": {
      backgroundColor: "transparent",
    },
    "& .MuiCheckbox-root": {
      color: alpha(theme.palette.text.secondary, 0.35),
      "&.Mui-checked": { color: theme.palette.primary.main },
    },
  },

  meetingLable1: {
    backgroundColor: theme.palette.cardAdd.thirdBgColor,
    borderRadius: setCardBRadius,
    p: "24px",
    boxShadow: "none",
    border: `2px solid ${theme.palette.cardAdd.thirdBgColor}`,
    // color: theme.palette.orderListProp.lable1FontColor,
  },
  meetingLable2: {
    backgroundColor: theme.palette.cardAdd.thirdBgColor,
    borderRadius: setCardBRadius,
    p: "24px",
    boxShadow: "none",
    border: `2px solid ${theme.palette.cardAdd.thirdBgColor}`,
  },

  mL1Heading: {
    fontSize: "15px",
    fontWeight: 600,
    // color: theme.palette.orderListProp.lable1FontColor,
  },
  mL1Heading2: {
    fontSize: "13px",
    fontWeight: 600,
    // color: theme.palette.orderListProp.lable1FontColor,
  },
  mL2Heading: {
    fontSize: "15px",
    fontWeight: 600,
    // color: theme.palette.orderListProp.lable2FontColor,
  },

  mL1Subject: {
    fontSize: "15px",
    // color: theme.palette.orderListProp.lable1FontColor,
    whiteSpace: "pre-line",
    lineHeight: 1.1,
  },
  mL2Subject2: {
    fontSize: "15px",
    // color: theme.palette.orderListProp.lable2FontColor,
    whiteSpace: "pre-line",
    lineHeight: 1.1,
  },
  upMeetingTypograpytext: {
    fontSize: "15px",
    // color: theme.palette.iconButton.editIconColor,
    whiteSpace: "pre-line",
    lineHeight: 1.1,
    fontWeight: 600,
  },
  mLButtonUpdate: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    lineHeight: 1.5,
    backgroundColor: theme.palette.iconButton.editButtonColor,
    borderRadius: setButtonBRadius,
    // boxShadow: "0px 2px 6px 0px",
    // color: '#aab8c5',
    color: theme.palette.iconButton.editIconColor,
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  mL12ButtonView: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    lineHeight: 1.5,
    backgroundColor: theme.palette.iconButton.viewButtonColor,
    borderRadius: setButtonBRadius,
    // boxShadow: "0px 2px 6px 0px",
    // color: '#aab8c5',
    color: theme.palette.iconButton.viewIconColor,
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  mL12ButtonDelate: {
    paddingX: "1rem",
    paddingY: "0.5rem",
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    lineHeight: 1.5,
    backgroundColor: theme.palette.iconButton.deleteButtonColor,
    borderRadius: setButtonBRadius,
    // boxShadow: "0px 2px 6px 0px",
    // color: '#aab8c5',
    color: theme.palette.iconButton.deleteIconColor,
    cursor: "pointer",
    transition:
      "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
    },
    "&:focus": {
      boxShadow: "0 0 0 0.15rem rgba(var(--bs-btn-focus-shadow-rgb), .5)",
    },
    "&:disabled": {
      opacity: 0.65,
    },
  },
  todocard: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    p: "24px",
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid #e0e0e0",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  },
  todobox: {
    px: 3,
    py: 2,
    borderBottom: "1px solid #e0e0e0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  stepperCardProductView: {
    boxShadow: "none",
    backgroundColor: theme.palette.cardAdd.bgColor,
    borderRadius: setCardBRadius,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    fontSize: "16px",
    p: 5,
    fontWeight: 400,
  },
  stepperCardProductView2: {
    boxShadow: "none",
    borderRadius: 3,
    backgroundColor: theme.palette.cardAdd.bgColor,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  stackProp: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
  },
  profileViewPageBackColor: {
    boxShadow: "none",
    backgroundColor: "#e8f1f0",
    borderRadius: setCardBRadius,
    fontSize: "12px",
    p: 5,
    fontWeight: 400,
  },
  orderNumberBox: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: `1px solid #fff`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  orderNumberBoxEmail: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: `1px solid ${theme.palette.text.primary}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  orderNumberBox2: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: `1px solid ${theme.palette.primary.main}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  DialogMobile: {
    backgroundColor: theme.palette.background.default,
    borderRadius: setBRadius,
    padding: "10px",
  },
  mobileListText: {
    fontSize: "12px",
    color: theme.palette.text.secondary,
  },
  orderLsitHeadingTypoColor: {
    color: theme.palette.heading.headingColor,
    fontWeight: 600,
  },
  orderLsitHeadingTypoColorPrimary: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  changedBranchColor: {
    color: "orange",
    fontWeight: 600,
  },
  changedStatusColor: {
    color: "blue",
    fontWeight: 600,
  },
  rightORTypoColor: {
    color: theme.palette.primary.main,
  },
  dhashboardCard: {
    backgroundColor: theme.palette.cardAdd.bgColor,
    borderRadius: setCardBRadius,
    height: "100%",
    color: "#424242",
    border: "1px solid #cdcdcd",
    // borderRadius: "16px",
  },
  dhashboardCardSkeleton: {
    backgroundColor: "#d0ddd0",
    borderRadius: setCardBRadius,
    height: "100%",
  },
  dhashboardIcon: {
    background: "#e0e0e0",
    borderRadius: "50%",
    width: 50,
    height: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mr: 2,
  },
  staffDhashboardSmallCard: {
    height: "100%",
    background: theme.palette.cardAdd.bgColor,
    boxShadow: "none",
    // color: theme.palette.primary.main,
    borderRadius: setCardBRadius,
    border: "1px solid #cdcdcd",
  },
  staffDhashboardSmallCardHeading: {
    fontSize: "calc(1.3125rem + 0.75vw)",
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.palette.primary.main,
  },
  staffDhashboardSmallCardSubHeading: {
    fontSize: "0.9375rem",
    fontWeight: 600,
    lineHeight: 1.1,
    color: theme.palette.heading.headingColor,
  },
  staffBirthdayTabList: {
    background: theme.palette.cardAdd.bgColor,
    display: "flex",
    alignItems: "center",
    p: "2px",
    cursor: "pointer",
    transition: "background 0.2s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },

  staffDhashboardSmallCardUpdate: {
    height: "100%",
    background: theme.palette.cardAdd.bgColor,
    boxShadow: "none",
    borderRadius: setCardBRadius,
    padding: "24px",
  },
  staffDhashboardSmallCardUpdateNonoe: {
    height: "100%",
    background: theme.palette.cardAdd.bgColor,
    boxShadow: "none",
    borderRadius: setCardBRadius,
    padding: "none",
  },
  dashboardfristCardGrid: {
    height: "100%",
    alignItems: "center",
  },
  dashboardfristCardButton: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "32px",
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: "0.75rem",
    color: theme.palette.primary.main,
    border: "none",
    padding: 5,
    width: "60px",
    height: "60px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: color(theme.palette.primary.main).lighten(0.6).hex(),
    "&:active": {
      backgroundColor: theme.palette.primary.main,
      "& .MuiSvgIcon-root": {
        color: "white",
      },
    },
  },
  dashboardfristCardButtonIcon: {
    fontSize: "32px",
  },
  dashboardfristCardButtonText1: {
    fontSize: "14px",
    fontWeight: 400,
    textAlign: "right",
  },
  dashboardfristCardButtonText2: {
    fontSize: "24px",
    fontWeight: 600,
    textAlign: "right",
  },
  dashboardfristCardHeading: {
    fontSize: "14px",
    fontWeight: 600,
  },
  dashboardtable: {
    width: "100%",
    // backgroundColor: theme.palette.cardAdd.bgColor,
    verticalAlign: "top",
    "& > :not(caption) > * > *": {
      padding: "none",
    },
  },
  dashboardtableHeaderRow: {
    backgroundColor: theme.palette.cardAdd.thirdBgColor,
  },
  dashboardtablheader: {
    color: theme.palette.cardAdd.headingColor,
    borderBottom: `1px solid ${theme.palette.cardAdd.borderColor}`,
  },
  menuListItemText: {
    fontSize: "14px",
  },
  chipButton: {
    fontWeight: 600,
    borderWidth: 1.5,
    // borderRadius: setButtonBRadius,
  },

  
  bellActive: {
    animation: "$bellShake 0.6s ease-in-out",
  },

  // Pulse animation for badge
  badgePulse: {
    animation: "$badgePulse 1.5s infinite",
  },

  // Keyframes
  "@keyframes bellShake": {
    "0%": { transform: "rotate(0)" },
    "15%": { transform: "rotate(10deg)" },
    "30%": { transform: "rotate(-10deg)" },
    "45%": { transform: "rotate(10deg)" },
    "60%": { transform: "rotate(-10deg)" },
    "75%": { transform: "rotate(4deg)" },
    "100%": { transform: "rotate(0)" },
  },

  "@keyframes badgePulse": {
    "0%": { boxShadow: "0 0 0 0 rgba(244, 67, 54, 0.7)" },
    "70%": { boxShadow: "0 0 0 10px rgba(244, 67, 54, 0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(244, 67, 54, 0)" },
  },

  editButtonProfileActive: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: "4px",
    color: "#ffffff",
    border: "none",
    padding: 5,
    minWidth: 60,
    height: 55,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: "#004274",
      "& .MuiSvgIcon-root": {
        color: "white",
      },
    },
    "&:active": {
      backgroundColor: "#004274",
      "& .MuiSvgIcon-root": {
        color: "white",
      },
    },
  },

  editButtonProfile: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: setButtonBRadius,
    color: theme.palette.iconButton.editIconColor,
    border: "none",
    padding: 5,
    minWidth: 50,
    height: 50,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.iconButton.editButtonColor,
    "&:hover": {
      backgroundColor: theme.palette.iconButton.editIconColor,
      "& .MuiSvgIcon-root": {
        color: theme.palette.iconButton.editButtonColor,
      },
    },
    "&:active": {
      backgroundColor: theme.palette.primary.main,
      "& .MuiSvgIcon-root": {
        color: "white",
      },
    },
  },

  buttonGroup11: {
    display: "flex",
    justifyContent: "left",
    gap: "7px",
    marginTop: "16px",
  },

  
  buttonItem11: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: 67
  },

  editButtonProfileActive11: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: "4px",
    color: "#ffffff",
    border: "none",
    padding: 5,
    minWidth: 60,
    height: 55,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: "#004274",
      "& .MuiSvgIcon-root": {
        color: "white",
      },
    },
    "&:active": {
      backgroundColor: "#004274",
      "& .MuiSvgIcon-root": {
        color: "white",
      },
    },
  },
  editButtonProfile11: {
    px: "1rem",
    py: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: "4px",
    color: "#ffffff",
    border: "none",
    padding: 5,
    minWidth: 60,
    height: 55,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#004274",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      "& .MuiSvgIcon-root": {
        color: "white",
      },
    },
    "&:active": {
      backgroundColor: theme.palette.primary.main,
      "& .MuiSvgIcon-root": {
        color: "white",
      },
    },
  },
  drawerHeaderText: {
    color: theme.palette.topbarButton.drawerMenuText,
  },
  drawerNotification: {
    backgroundColor: theme.palette.mainMenuNav.mainNavBg,
  },
}));
